/**
 * THE ONE PLACE THIS INTERFACE TALKS TO THE SERVER.
 *
 * Two jobs, and the second one is the point of the file.
 *
 * JSON in, JSON out, same origin. In production the SPA and the Hono app are one Worker, so every
 * path here is written as `/api/...` and there is no base URL to configure, no CORS and no token
 * to attach. Identity is Microsoft Entra, handled by the Worker itself at `/api/auth`: the sign-in
 * leaves a session cookie behind, and every call below rides on that cookie and nothing else.
 *
 * AND IT KEEPS THE SERVER'S SENTENCE. The API answers a failure as `{ error, message }`, and
 * `api/src/routes/helpers.ts` says why the message is written the way it is: Victoria reads it in
 * a car between houses, and "invalid input" costs her a phone call to Alex. So a failure here
 * becomes an ApiError carrying that exact sentence through to the screen. Swallowing it and
 * showing "something went wrong" throws away the most carefully written text in the system.
 *
 * Every function below matches an endpoint that exists in `api/src/routes/`. There are no others.
 */
import type {
  AccessGrantTarget,
  AccessSecretKind,
  AgreementState,
  AssignmentStatus,
  AuthorizationDoor,
  AuthorizationRevokeReason,
  BreachState,
  CallOffChoice,
  CallOffReason,
  CallOffReportedVia,
  Cadence,
  CallOutcome,
  CampaignStatus,
  CaptureMethod,
  Channel,
  ChargeCancelReason,
  ChargeDecision,
  ChargeGateReason,
  ChargeHoldReason,
  ChoiceOwner,
  Classification,
  CleanerStatus,
  ClientLanguage,
  ClientStatus,
  ClosedDateKind,
  ComposerKind,
  ConsentKind,
  ConsentPurpose,
  ContactRole,
  County,
  DeepCleanBasis,
  DiscardReason,
  DocumentKind,
  DocumentState,
  DoNotContactDoor,
  DoNotContactKind,
  DoNotContactReason,
  DoNotContactTarget,
  EmailGoLiveState,
  EmailKind,
  EmailState,
  EndedReason,
  EnquiryState,
  EntrySource,
  ExpenseCategory,
  FailureCode,
  FeedbackFace,
  FeedbackSource,
  FollowUpChannel,
  HeldBackReason,
  HerChargeStage,
  HomePlanSource,
  HomeRequestCancelReason,
  HomeRequestKind,
  HomeRequestState,
  InboundKeyword,
  InboundMessageState,
  InvoiceChargeState,
  InvoiceState,
  IssueKind,
  IssueState,
  KeyCustodyEvent,
  KeyKind,
  LiftReason,
  LinkPayPage,
  Lock,
  MembershipRevokeReason,
  MembershipRole,
  MembershipState,
  MessageChannel,
  MessageIntentState,
  NoticeMode,
  NotificationKind,
  NotificationState,
  OfficeMarkReason,
  OfficePreferenceHow,
  OfficeRevokeReason,
  PaymentKind,
  PaymentMethod,
  PayPageKind,
  PayScreen,
  PhoneForgetOutcome,
  PreferenceCategory,
  PreferenceHow,
  PreferredChannel,
  PriceSource,
  PromiseCode,
  RateLine,
  Remedy,
  Role,
  SavedMethodType,
  SendTime,
  Sensitivity,
  ServiceLine,
  SetupPageState,
  SilenceReason,
  SmsGoLiveState,
  StopHoldOutcome,
  StopWordsChannel,
  SurveyHoldReason,
  SurveyState,
  SuppressionScope,
  SystemLink,
  TaskKind,
  TaskState,
  TextingRegrantSkip,
  TextsState,
  TimelineKind,
  TimelineView,
  TouchDirection,
  TouchOutcome,
  TurnOnBy,
  UserStatus,
  VendorRegistrationState,
  VisitChangeKind,
  VisitKind,
  VisitOrigin,
  VisitStatus,
  WritableAccessSecretKind,
} from '@fk/shared';

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    /**
     * Where to send the browser to sign in, when the server sent one: on the 403
     * `step_up_required` with the return path already inside it, and on the 401 `not_signed_in`.
     * A screen renders what the server composed and never builds one of its own.
     */
    readonly signInPath: string | null = null,
    /**
     * The whole answer the refusal came in, for the few that carry more than a sentence: the lift of
     * a do-not-contact mark refused `beneath` names the sets standing under it. Null when none came.
     */
    readonly body: unknown = null,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/** True for the one refusal that is answered with a link rather than a sentence. */
export const needsStepUp = (error: ApiError): boolean =>
  error.status === 403 && error.code === 'step_up_required' && error.signInPath !== null;

/**
 * TRUE FOR THE THREE REFUSALS THAT MEAN THIS BROWSER IS NOBODY.
 *
 * `not_signed_in` is the 401 with no session at all, `session_revoked` the 403 for a session
 * somebody ended on purpose, `not_enrolled` the 403 for an account Microsoft signed in and this
 * system has never heard of. All three come out of `api/src/middleware/identity.ts`, and none of
 * the three is fixed by repeating the request: every other screen is the same locked door.
 *
 * Step-up is deliberately not one of them. That session is alive, everything else still works, and
 * the screen that asked answers it where it stands with `needsStepUp` above.
 */
export const needsSignIn = (error: ApiError): boolean =>
  (error.status === 401 || error.status === 403) &&
  (error.code === 'not_signed_in' ||
    error.code === 'session_revoked' ||
    // Since ops #74 item 1 the row holds the cookie's own thirty days, so a request in the moment
    // between the row's end and the browser dropping the cookie is refused this way.
    error.code === 'session_expired' ||
    error.code === 'not_enrolled');

type SessionEndedListener = (error: ApiError) => void;

const sessionEndedListeners = new Set<SessionEndedListener>();

/**
 * SAID OUT LOUD WHEN A SESSION ENDS UNDER SOMEBODY'S HANDS, so the root can put the sign-in screen
 * up instead of leaving her at a door with a button.
 *
 * A screen catching its own failure has no way to know the difference between "that visit is not
 * here" and "you are nobody now", and it drew both the same way: a sentence and "Try again", which
 * re-issues the request that just failed. The sentence is usually an instruction to sign in, and
 * the one screen in this application with a sign-in link was unreachable without reloading the
 * document by hand. So the one place that talks to the server is the place that notices, and
 * `App.tsx` is the one thing listening. Returns the way to stop listening.
 */
export function onSessionEnded(listener: SessionEndedListener): () => void {
  sessionEndedListeners.add(listener);
  return () => {
    sessionEndedListeners.delete(listener);
  };
}

const JSON_HEADERS = { 'content-type': 'application/json' };

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  let response: Response;
  try {
    response = await fetch(path, init);
  } catch {
    // A phone between two houses loses signal, and that is not the same failure as a refusal.
    throw new ApiError(
      0,
      'no_answer',
      'The network did not answer. Check the signal and try that again.',
    );
  }

  const text = await response.text();
  let body: unknown = null;
  if (text) {
    try {
      body = JSON.parse(text);
    } catch {
      body = null;
    }
  }

  if (!response.ok) {
    const shape = body as { error?: string; message?: string; signInPath?: string } | null;
    const refusal = new ApiError(
      response.status,
      shape?.error ?? 'error',
      shape?.message ?? `The server answered ${response.status} and said nothing more.`,
      typeof shape?.signInPath === 'string' ? shape.signInPath : null,
      body,
    );
    // Told before it is thrown: the screen that asked still gets it and still says what it was
    // doing, but the root has already decided that what somebody needs now is the way back in.
    if (needsSignIn(refusal)) for (const listener of sessionEndedListeners) listener(refusal);
    throw refusal;
  }

  return body as T;
}

const send = <T>(path: string, method: string, body: unknown): Promise<T> =>
  request<T>(path, { method, headers: JSON_HEADERS, body: JSON.stringify(body) });

/** A query string from the entries that have a value. Every value goes through encodeURIComponent. */
function query(params: Record<string, string | null | undefined>): string {
  const parts: string[] = [];
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === '') continue;
    parts.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
  }
  return parts.length > 0 ? `?${parts.join('&')}` : '';
}

/* ------------------------------------------------------------------ *
 * What the endpoints return. Projections of the real rows: only the
 * columns a screen reads are named, so an unused column cannot quietly
 * become a dependency.
 * ------------------------------------------------------------------ */

export interface Me {
  readonly userId: string;
  readonly displayName: string;
  readonly email: string | null;
  readonly role: Role;
  readonly roleLabel: string;
  readonly cleanerId: string | null;
  /** The server's calendar day, so a screen saying "today" and the schedule agree. */
  readonly today: string;
  /**
   * Whether this deployment can hand a message to the phone system. Until step 7 wires that seam
   * the move flow offers the typed reasons only, and says why.
   */
  readonly canHandOffMessages: boolean;
  /**
   * Whether the invoice screen offers a pay link at all: this deployment holds a Stripe key, and
   * this is the office. Without it there is no "Get a pay link" and nothing is asked for.
   */
  readonly payLinks: boolean;
  /**
   * Whether a pay link may go by text (ops #21). Off until an owner allows it, so the invoice screen
   * offers email alone; always false for a cleaner, who sends none.
   */
  readonly payLinksByText: boolean;
  /**
   * Where ops's own go-live switch for texts stands (ops #31), for the office alone: a cleaner sends
   * no text and is told none of it. The move flow and the invoice screen offer a text only where it
   * would go and say where the switch stands; the server refuses whatever they offer. Absent reads as
   * off, the safe direction.
   */
  readonly smsGoLive?: SmsGoLive;
  /**
   * Where ops's own go-live switch for client email stands (ops #76 O6), for the office alone, beside
   * texting's: a screen offers an email only where it would go, and the server refuses whatever it
   * offers. Absent reads as off, the safe direction.
   */
  readonly emailGoLive?: EmailGoLive;
  /**
   * Whether this deployment is connected to home (ops #19), its url and its secret both, so the
   * household screen offers "Invite to home" only where the invitation could go. The office's routes
   * decide either way, refusing `409 home_not_connected`, and absent reads as off, the safe direction.
   */
  readonly homeSeam?: boolean;
  /**
   * Whether Call through the business line places a call from this deployment (ops #84 phase 6):
   * `place_call` switched on for ops and the phone system's door set, for the office alone. Absent
   * reads as off, the safe direction: the page says plainly the line is not switched on yet, and the
   * door refuses `409 calls_off` whatever is drawn.
   */
  readonly placeCalls?: boolean;
  /**
   * The doors to the other systems (2026-10-17), handed to an owner or a manager alone: the phone
   * system's wall and records, growth's panel and the website's leads screens, as this deployment's
   * `SYSTEM_LINKS` declares them. Absent for the office and a cleaner, which the server decides; the
   * top bar draws the wall as its door and the rest behind the Systems menu.
   */
  readonly systems?: readonly SystemLink[];
}

/** The go-live switch for client email as `GET /api/me` tells the office of it (ops #76 O6). */
export interface EmailGoLive {
  readonly state: EmailGoLiveState;
  /** The words an email parked by the switch keeps, for no address in particular; null when on. */
  readonly refusal: string | null;
  /** The addresses an email may go to while the switch is at test addresses; empty otherwise. */
  readonly addresses: readonly string[];
}

/** The go-live switch as `GET /api/me` tells the office of it (ops #31). */
export interface SmsGoLive {
  readonly state: SmsGoLiveState;
  /** The words a text parked by the switch keeps, for no number in particular; null when on. */
  readonly refusal: string | null;
  /** The numbers a text may go to while the switch is at test handsets; empty otherwise. */
  readonly handsets: readonly string[];
  /** The phone system's newest word that texting is not enabled there, while ops is not on. */
  readonly phoneSystemSaid: string | null;
}

export interface ClientRow {
  readonly id: string;
  readonly displayName: string;
  readonly status: ClientStatus;
  readonly sourceChannel: Channel;
  readonly sourceDetail: string | null;
  /**
   * Who introduced her, as an id and never as words. At most one of the two: a referral credited
   * twice is a referral paid twice, and the table says so with `ck_clients_referrer_single`.
   */
  readonly referredByClientId: string | null;
  readonly referredByProspectId: string | null;
  readonly reviewUrl: string | null;
  readonly endedOn: string | null;
  readonly createdAt: string;
  /**
   * THE LANGUAGE THE HOUSEHOLD READS (ops #98), `en` until the office sets `es` on the client's
   * word. On the household's own read and not on the book's rows. OPTIONAL BECAUSE A GRADUAL
   * DEPLOYMENT SPLITS CODE: absent draws nothing and offers no change.
   */
  readonly language?: ClientLanguage;
  /**
   * WHICH TOWN HER HOME IS IN, on the row, from a correlated subquery in the same statement. It
   * lives on `sites` rather than on `clients`, so the book used to fetch a whole household per row
   * to read one word off it. Null where nobody has typed a home in yet, and where a household has
   * more than one it is the oldest live one, which is the home she was typed in with.
   */
  readonly town: string | null;
}

/**
 * WHAT THE REGISTER SAYS ABOUT ONE PERSON, when the household read carries it.
 *
 * Suppression is a union and consent is an intersection, so the two booleans are the whole answer:
 * nothing is sent on a channel she has not agreed to, and a standing stop refuses whatever the
 * consent rows say. The other two fields are the evidence, which is the half a person reads: the
 * day it was given and how it was captured. For a consent given on a call, that is the only
 * evidence there will ever be.
 */
export interface ContactMessaging {
  readonly maySms: boolean;
  readonly mayEmail: boolean;
  /** The day the position in force was captured, when there is a row at all. */
  readonly recordedOn: string | null;
  readonly captureMethod: CaptureMethod | null;
  /**
   * The do-not-contact mark in force on each channel (ops #44), on her, her number or her address,
   * and who recorded it by name. OPTIONAL BECAUSE A GRADUAL DEPLOYMENT SPLITS CODE: absent draws no
   * mark, which is what a Worker older than the field drew.
   */
  readonly doNotContact?: MarksHeld;
  /**
   * The stop in her own words held on her number for the office (ops #30), as every screen reads one,
   * with the words of the text that opened it, or null when none is open. Optional for the same
   * gradual deployment: absent draws no hold.
   */
  readonly hold?: HeldOnHer | null;
  /**
   * WHAT HER CHOICES AND THE OFFICE'S HOLDS WOULD DO to a message of each category, at every home, on
   * each channel (ops #76 phase 2, `docs/plan-preferences.md` section 7), for the screens that send
   * from the household's page. Optional for the same gradual deployment: absent says nothing.
   */
  readonly choices?: Partial<Record<PreviewCategory, ChoicePreview>>;
}

/** The categories the household's page previews, one a category that files a message. */
export type PreviewCategory =
  'visit_changes' | 'visit_reminders' | 'visit_day' | 'invoices' | 'receipts';

/** What a screen shows before the office presses send, on one channel (ops #76 phase 2). */
export interface ChannelPreview {
  /** The words the message would be parked with, led by `not sent: `, or null where it goes. */
  readonly notSent: string | null;
  /** Where it would be parked and the office given the call instead. */
  readonly officeCalls: boolean;
}

/** Both channels' answer for one person and one kind. */
export interface ChoicePreview {
  readonly email: ChannelPreview;
  readonly sms: ChannelPreview;
}

/**
 * A STOP IN HER OWN WORDS, HELD FOR THE OFFICE (ops #30), as every read sends one (ops #33): the phrase
 * found and what it asked, when she said it, the two clocks as days in Florida, which the server
 * works out so a phone in another zone reads the Thursday the tick will act on, and how it ended and
 * by whom. Never the number, which the hold keeps in clear through a forget.
 */
export interface StopHoldView {
  readonly id: string;
  /** The phrase found in her text, in canonical form: found, and never a quotation of her. */
  readonly words: string;
  /** `sms`, or `all` for words asking to be left alone on every channel. */
  readonly wordsChannel: StopWordsChannel;
  readonly saidAt: string;
  readonly saidOn: string;
  readonly openedAt: string;
  readonly alertedAt: string | null;
  readonly alertedOn: string | null;
  readonly alertDueOn: string;
  readonly suppressDueOn: string;
  /** Open, and nobody decided in five business days: the next tick makes it a stop. */
  readonly suppressDue: boolean;
  readonly outcome: StopHoldOutcome | null;
  readonly outcomeAt: string | null;
  readonly outcomeOn: string | null;
  readonly outcomeBy: string | null;
  readonly outcomeByName: string | null;
  readonly outcomeNote: string | null;
  readonly markId: string | null;
}

/** The hold on her number as her household's read carries it, with the words of her text. */
export type HeldOnHer = StopHoldView & { readonly body: string | null };

/**
 * ONE OPEN HOLD ON THE TEXTS SCREEN'S BAND: the hold, her text as the list draws one, whose it is by
 * name, and the marks on her and her number, which say what still refuses texts once it is cleared.
 */
export interface StopHoldRow {
  readonly hold: StopHoldView;
  readonly text: {
    readonly id: string;
    readonly phoneMessageId: string;
    readonly fromE164: string | null;
    readonly body: string | null;
    readonly redacted: readonly string[];
    readonly mediaCount: number;
    readonly keyword: InboundKeyword | null;
    readonly receivedAt: string;
    readonly handled: InboundMessageState;
  };
  readonly clientId: string | null;
  readonly clientName: string | null;
  readonly contactId: string | null;
  readonly contactName: string | null;
  readonly enquiryId: string | null;
  readonly enquiryName: string | null;
  readonly phonePage: string | null;
  readonly doNotContact: MarksHeld;
}

export interface StopHoldList {
  readonly limit: number;
  readonly holds: readonly StopHoldRow[];
}

/** The open holds, oldest said first. Office only. */
export const listStopHolds = (): Promise<StopHoldList> => request('/api/stop-holds');

/**
 * THE OFFICE'S DECISION ON A HOLD. A confirmation names a channel only where it differs from her
 * words, and then with a note; a clear always has one. Who may press which is the server's: a clear,
 * and reading "leave me alone" as texts only, are an owner's or a manager's.
 */
export type StopHoldDecision =
  | {
      readonly outcome: 'confirmed';
      readonly channel?: StopWordsChannel;
      readonly note?: string | null;
    }
  | { readonly outcome: 'cleared'; readonly note: string };

export type StopHoldAnswer =
  | { readonly outcome: 'confirmed'; readonly markId: string; readonly hold: StopHoldView }
  | {
      readonly outcome: 'cleared';
      readonly hold: StopHoldView;
      /** What still refuses texts to her after the clear, which a clear never lifts. */
      readonly stillRefused: MarksHeld;
    };

export const decideStopHold = (id: string, decision: StopHoldDecision): Promise<StopHoldAnswer> =>
  send(`/api/stop-holds/${encodeURIComponent(id)}`, 'POST', decision);

/* ------------------------------------------------------------------ *
 * The do-not-contact register (ops #44): marks set and lifted, who,
 * when and why. `api/src/routes/do-not-contact.ts` is the door.
 * ------------------------------------------------------------------ */

/** A channel a mark holds on: every scope but `all`, which is a word about all of them. */
export type MarkChannel = Exclude<SuppressionScope, 'all'>;

/** The set in force on one channel: which mark, on which of her keys, why, who and since when. */
export interface HoldingMark {
  readonly id: string;
  readonly target: DoNotContactTarget;
  readonly reason: DoNotContactReason;
  readonly door: DoNotContactDoor;
  readonly recordedBy: string | null;
  /** Who recorded it, by name, where the read carries it: the household page does. */
  readonly recordedByName?: string | null;
  readonly saidAt: string;
  /** The day she said it, in Florida. */
  readonly saidOn: string;
}

/** Per channel, the set that refuses it, or null when nothing does. */
export type MarksHeld = Readonly<Record<MarkChannel, HoldingMark | null>>;

/** One row of a target's history, newest first, the note included: only this read carries it. */
export interface MarkHistoryRow {
  readonly id: string;
  readonly targetKind: DoNotContactTarget;
  readonly contactId: string | null;
  readonly phoneE164: string | null;
  readonly email: string | null;
  readonly channel: SuppressionScope;
  readonly kind: DoNotContactKind;
  readonly reason: DoNotContactReason;
  readonly note: string | null;
  readonly door: DoNotContactDoor;
  readonly recordedBy: string | null;
  readonly recordedByName: string | null;
  readonly saidAt: string;
  readonly createdAt: string;
}

/** Who a mark is set on: exactly one of her keys. */
export type MarkTarget =
  { readonly contactId: string } | { readonly phone: string } | { readonly email: string };

/** A mark the office sets. Anybody in the office; the server says so either way. */
export interface MarkSet {
  readonly target: MarkTarget;
  readonly channel: SuppressionScope;
  readonly reason: OfficeMarkReason;
  readonly note: string | null;
  /** When she said it, an instant, or null for now. */
  readonly saidAt: string | null;
}

/** A mark an owner or a manager lifts, by its id. The server refuses anybody else. */
export interface MarkLift {
  readonly markId: string;
  readonly reason: LiftReason;
  readonly note: string | null;
  readonly saidAt: string | null;
  /**
   * The sets standing beneath the mark that this lift releases with it, by id, exactly as the
   * door's `beneath` refusal named them. Left off for a mark with nothing beneath it.
   */
  readonly alsoLifting?: readonly string[];
}

/** A set standing beneath the mark a lift names, on the channels the lift would release it from. */
export interface BeneathMark {
  readonly mark: HoldingMark;
  readonly channels: readonly MarkChannel[];
}

/**
 * The sets a lift was refused for, from the door's `409 beneath`, or null for any other refusal: a
 * lift releases every set standing on its channel, so the door names them and lifts them only named.
 */
export function beneathOf(error: unknown): readonly BeneathMark[] | null {
  if (!(error instanceof ApiError) || error.code !== 'beneath') return null;
  const said = (error.body as { beneath?: unknown } | null)?.beneath;
  return Array.isArray(said) ? (said as BeneathMark[]) : null;
}

/** The history of a person, her number and her address, in one answer. */
export const markHistory = (keys: {
  readonly contact?: string | null;
  readonly phone?: string | null;
  readonly email?: string | null;
}): Promise<{ marks: readonly MarkHistoryRow[] }> =>
  request(
    `/api/do-not-contact${query({ contact: keys.contact, phone: keys.phone, email: keys.email })}`,
  );

/** A mark set; `written` false when the same mark already held and nothing was written. */
export const setMark = (mark: MarkSet): Promise<{ id: string; written: boolean }> =>
  send('/api/do-not-contact', 'POST', { kind: 'set', ...mark });

/**
 * What a lift answers: the rows written, one for every channel or one for each it still held; and,
 * where it opened texts to a number the phone system was told to refuse, that number and the door's
 * sentence, because nothing tells the phone system yet that the texts may go again
 * (`faithfully-kept-phone#26`). Null where it did not, and absent from a Worker that predates it.
 */
export interface LiftAnswer {
  readonly ids: readonly string[];
  readonly phoneMayStillRefuse?: { readonly number: string; readonly message: string } | null;
}

/** A mark lifted, as a row of its own. */
export const liftMark = (lift: MarkLift): Promise<LiftAnswer> =>
  send('/api/do-not-contact', 'POST', { kind: 'lifted', ...lift });

export interface ContactRow {
  readonly id: string;
  readonly clientId: string;
  readonly role: ContactRole;
  readonly displayName: string;
  readonly phoneE164: string | null;
  readonly email: string | null;
  readonly preferredChannel: PreferredChannel | null;
  /**
   * OPTIONAL BECAUSE THE REGISTER IS ITS OWN QUESTION. Absent means this read did not carry the
   * register's answer, which is not the same as her having agreed to nothing, and the household
   * page says so in exactly those terms rather than guessing either way. The one place the answer
   * is certain today is the move flow's preview, which asks the register directly.
   */
  readonly messaging?: ContactMessaging | null;
}

export interface SiteRow {
  readonly id: string;
  readonly clientId: string;
  readonly label: string | null;
  readonly addressLine1: string;
  readonly addressLine2: string | null;
  readonly town: string;
  readonly postalCode: string;
  readonly county: County;
  readonly communityName: string | null;
  readonly vendorRegistrationState: VendorRegistrationState;
  readonly vendorRegistrationExpiresOn: string | null;
  readonly bedrooms: number | null;
  readonly bathrooms: number | null;
  readonly squareFeet: number | null;
  readonly doNotList: string | null;
  readonly pets: string | null;
  readonly entryNotes: string | null;
  readonly notes: string | null;
  /** What they check first (ops #86): at most three lines, one thing each. Null when none was said. */
  readonly checksFirst?: string | null;
  /** The ninety-day deep-clean rule turns on this. Null means nobody has said. */
  readonly lastProfessionalCleanOn: string | null;
  readonly lastProfessionalCleanBasis: DeepCleanBasis | null;
}

export interface ClientRecord {
  readonly client: ClientRow;
  /**
   * The household that introduced this one, by name. The record carries the id; a screen showing
   * an id would be asking a person to hold a random string in her head, and the alternative is
   * this screen fetching the other household to read one word off it.
   */
  readonly referrerName: string | null;
  readonly contacts: readonly ContactRow[];
  readonly sites: readonly SiteRow[];
  /** What charges the household automatically, if anything does (ops #46, step 3b). */
  readonly autopay: AutopayView | null;
  /** While nothing stands, the method last stopped, when, by whom and why (2026-09-23). */
  readonly autopayStopped: StoppedAutopay | null;
  /**
   * The newest page she asked for at home to save a method with nothing owed, where it stands (ops
   * #76 item 5). OPTIONAL BECAUSE A GRADUAL DEPLOYMENT SPLITS CODE: absent draws nothing of it.
   */
  readonly setupPage?: SetupPageView | null;
}

/**
 * WHERE A PAGE SHE ASKED FOR AT HOME TO SAVE A METHOD STANDS (ops #76 item 5), read by the server from
 * the row and the clock: being made, open at Stripe, saved on Stripe's page and not recorded yet (a
 * bank account waiting on its small deposits), saved and recorded, or ended without saving.
 */
export type SetupPageStanding = 'making' | 'open' | 'checking' | 'saved' | 'ended';

export interface SetupPageView {
  readonly kind: LinkPayPage;
  readonly state: SetupPageState;
  readonly standing: SetupPageStanding;
  /** The person it was made for, while she is on the household. */
  readonly contactName: string | null;
  readonly claimedAt: string;
  readonly expiresAt: string | null;
  readonly completedAt: string | null;
  /** The saved method it recorded, once it has. */
  readonly authorizationId: string | null;
}

/**
 * THE METHOD LAST STOPPED FOR A HOUSEHOLD WITH NONE STANDING (the staging walk of 2026-09-23): after
 * the Stop the household said nothing about the method at all. Which it was, when it stopped, who at
 * the office pressed Stop, or nobody when Stripe or ops itself did, the door, and why.
 */
export interface StoppedAutopay {
  readonly displayLabel: string;
  readonly methodType: SavedMethodType | null;
  readonly stoppedAt: string;
  readonly stoppedByName: string | null;
  readonly stoppedVia: AuthorizationDoor | null;
  readonly reason: AuthorizationRevokeReason | null;
}

/**
 * THE SAVED METHOD THAT STANDS FOR A HOUSEHOLD (ops #46, step 3b): she ticked Stripe's own save box on
 * a page ops made, and this is what the office reads it by. The card number and the account number
 * are Stripe's and never here: a label, the kind, since when, and who agreed.
 */
export interface AutopayView {
  readonly authorizationId: string;
  /** "Visa 4242", "Apple Pay Visa 4242", "Chase checking 6789", "Link". */
  readonly displayLabel: string;
  readonly methodType: SavedMethodType | null;
  /** When she agreed: the moment the page she ticked the box on completed. */
  readonly since: string;
  /** Who agreed, by name, while she is on the household. */
  readonly contactName: string | null;
}

/** What the stop door answers: the row as it stands now, and whether this press stopped it. */
export interface AutopayStopped {
  readonly id: string;
  readonly state: string;
  readonly revokedAt: string | null;
  readonly revokedVia: string | null;
  readonly revokeReason: AuthorizationRevokeReason | null;
  readonly stopped: boolean;
}

export interface CleanerRow {
  readonly id: string;
  readonly displayName: string;
  readonly phoneE164: string | null;
  readonly status: CleanerStatus;
  readonly classification: Classification | null;
  readonly classificationChangedOn: string | null;
  readonly startedOn: string | null;
  readonly endedOn: string | null;
  /**
   * THE OWNERS' MARK THAT THE OFFICE'S OWN CHECKS ARE COMPLETE, and the one weekly slot kept open for
   * make-ups (ops #85). OPTIONAL BECAUSE A GRADUAL DEPLOYMENT SPLITS CODE: absent reads as unmarked and
   * no slot, which is what a Worker older than the fields held.
   */
  readonly checksCompleteOn?: string | null;
  readonly makeUpWeekday?: number | null;
  readonly makeUpWindowStartLocal?: string | null;
  readonly makeUpWindowEndLocal?: string | null;
}

/** One call-off as a cleaner's page lists it for the office (ops #85). */
export interface CallOffOfCleaner {
  readonly id: string;
  readonly visitId: string;
  readonly day: string | null;
  readonly reason: CallOffReason;
  readonly heardAt: string;
  readonly choice: CallOffChoice | null;
  readonly visitChangeId: string | null;
}

/**
 * Whether the owners' checks mark is set, and for a cleaner without it how much of the schedule is
 * still theirs from before every door asked for it (ops #85, 2026-10-22).
 */
export type ChecksOfCleaner =
  | { readonly complete: true }
  | { readonly complete: false; readonly liveSeries: number; readonly upcomingVisits: number };

/** One cleaner, and for the office the count of their call-offs in the last sixty days (ops #85). */
export interface CleanerDetail {
  readonly cleaner: CleanerRow;
  readonly callOffs?: {
    readonly inLast60Days: number;
    readonly rows: readonly CallOffOfCleaner[];
  };
  /** The office's alone; optional because a gradual deployment splits code, and absent draws nothing. */
  readonly checks?: ChecksOfCleaner;
}

export interface TermRow {
  readonly id: string;
  readonly cleanerId: string;
  readonly perCleanCents: number;
  readonly effectiveFrom: string;
  readonly effectiveTo: string | null;
}

export interface Catalog {
  readonly revision: number;
  readonly capturedOn: string;
  readonly sourceUrl: string;
  readonly contentHash: string;
  readonly lines: readonly RateLine[];
}

/**
 * A visit as the scoped reader returns it. The office row carries the price; a cleaner's row never
 * does, because the column is left out of her select rather than blanked afterwards. Optional here
 * for exactly that reason, and no cleaner screen reads it.
 */
export interface VisitRow {
  readonly id: string;
  readonly agreementId: string;
  readonly seriesId: string | null;
  readonly kind: VisitKind;
  readonly status: VisitStatus;
  readonly origin: VisitOrigin;
  readonly plannedDate: string | null;
  readonly scheduledDate: string;
  /** ISO UTC instants. Rendered in America/New_York, never through Date getters. */
  readonly windowStart: string | null;
  readonly windowEnd: string | null;
  readonly startedAt: string | null;
  readonly finishedAt: string | null;
  readonly askNothing: boolean;
  readonly isRecleanForVisitId?: string | null;
  readonly priceCents?: number;
  readonly priceSource?: PriceSource;
}

/** Who is on a visit. The office also gets her phone; a co-worker's row is a name and a status. */
export interface CrewOnVisit {
  readonly id: string;
  readonly visitId: string;
  readonly cleanerId: string;
  readonly displayName: string;
  readonly status: AssignmentStatus;
  readonly phoneE164?: string | null;
  /**
   * When they pressed Accept or Decline in the app, and the reason they gave for a decline (ops #74
   * item 5). The office's alone, never a crewmate's; optional because a Worker older than the field
   * carries neither, and absent draws no day and no reason.
   */
  readonly respondedAt?: string | null;
  readonly declineReason?: string | null;
}

export interface VisitWithCrew extends VisitRow {
  readonly cleaners: readonly CrewOnVisit[];
}

export interface VisitList {
  readonly from: string;
  readonly to: string;
  readonly today: string;
  readonly visits: readonly VisitWithCrew[];
}

/** The person the Today screen rings. */
export interface PrimaryContact {
  readonly clientId: string;
  readonly displayName: string;
  readonly phoneE164: string | null;
  readonly preferredChannel: PreferredChannel | null;
}

/** A change recorded and not yet told: the client does not know, and the calendar shows it. */
export interface PendingChange {
  readonly id: string;
  readonly visitId: string;
  readonly kind: VisitChangeKind;
  readonly toDate: string | null;
  readonly createdAt: string;
}

/** One row of the office Today screen: the visit joined to its home and household. */
export interface ScheduleRow extends VisitWithCrew {
  readonly priceCents: number;
  readonly priceSource: PriceSource;
  readonly serviceLine: ServiceLine;
  readonly cadence: Cadence;
  readonly siteId: string;
  readonly siteLabel: string | null;
  readonly addressLine1: string;
  readonly town: string;
  readonly postalCode: string;
  readonly clientId: string;
  readonly clientName: string;
  readonly arrivalNoticeMode: NoticeMode;
  readonly contact: PrimaryContact | null;
  readonly pendingChange: PendingChange | null;
  /**
   * A cleaner who cannot come, still waiting for the client's choice (ops #85). OPTIONAL BECAUSE A
   * GRADUAL DEPLOYMENT SPLITS CODE: absent draws nothing.
   */
  readonly callOff?: ScheduleCallOff | null;
  /**
   * The cleaners on a visit to come who have no owners' checks mark, placed before every door asked
   * for it (ops #85, 2026-10-22). Optional for the same reason: absent draws nothing.
   */
  readonly unmarkedCleaners?: readonly string[];
}

/** The open call-off on a visit, as the schedule carries it. */
export interface ScheduleCallOff {
  readonly id: string;
  readonly visitId: string;
  readonly cleanerId: string;
  readonly cleanerName: string;
  readonly reason: CallOffReason;
  readonly heardAt: string;
}

/** One cleaner's make-up slot on one day, open while no visit of theirs that day overlaps it. */
export interface MakeUpSlot {
  readonly cleanerId: string;
  readonly displayName: string;
  readonly day: string;
  readonly windowStartLocal: string;
  readonly windowEndLocal: string;
  readonly visitIds: readonly string[];
}

/**
 * A service line no new job sheet will open for on the business day, and at how many of its homes.
 * `scopeGaps` in `api/src/jobsheet/scopes.ts` works it out the way the door does, office only.
 */
export interface ScopeGap {
  readonly serviceLine: ServiceLine;
  /** Homes on this line where a visit will still ask for a sheet and `scopeFor` would find no words. */
  readonly homes: number;
  /** Homes on this line where a visit will still ask for a sheet at all. */
  readonly ofHomes: number;
}

export interface Schedule {
  readonly from: string;
  readonly to: string;
  readonly today: string;
  readonly visits: readonly ScheduleRow[];
  /**
   * OPTIONAL BECAUSE A GRADUAL DEPLOYMENT SPLITS CODE, so this screen can be answered by a Worker
   * older than the field. Absent draws no notice, which is what that Worker would have drawn.
   */
  readonly scopeGaps?: readonly ScopeGap[];
  /** Each cleaner's make-up slot in the range (ops #85); optional for the same reason. */
  readonly makeUpSlots?: readonly MakeUpSlot[];
}

export interface AgreementRow {
  readonly id: string;
  readonly siteId: string;
  readonly quoteId: string | null;
  readonly cardVersionId: string;
  readonly scopeVersionId: string | null;
  readonly serviceLine: ServiceLine;
  readonly cadence: Cadence;
  readonly rateLineCode: string;
  readonly priceCents: number;
  readonly priceSource: PriceSource;
  readonly state: AgreementState;
  readonly startedOn: string;
  readonly endedOn: string | null;
  readonly endedReason: EndedReason | null;
  readonly deepCleanDisclosedAt: string | null;
  readonly accessConfirmedAt: string | null;
  readonly createdAt: string;
}

export interface SeriesRow {
  readonly id: string;
  readonly agreementId: string;
  readonly anchorDate: string;
  readonly intervalDays: number;
  /** HH:MM in America/New_York. A time of day, not an instant. */
  readonly windowStartLocal: string;
  readonly windowEndLocal: string;
  readonly cleanerId: string | null;
  readonly pausedFrom: string | null;
  readonly pausedUntil: string | null;
  readonly holdSlotUntil: string | null;
  readonly materialisedTo: string | null;
  readonly endedOn: string | null;
}

export interface MessageIntentRow {
  readonly id: string;
  readonly visitChangeId: string | null;
  readonly contactId: string;
  readonly channel: MessageChannel;
  readonly state: MessageIntentState;
  readonly body: string;
  readonly handedOffAt: string | null;
  readonly refusedAt: string | null;
  readonly cancelledAt: string | null;
  /** Why it did not go, in the phone system's word; null while it may still go. */
  readonly failureCode: FailureCode | null;
  /** When the phone system's texting hours release it; null when nothing holds it. */
  readonly sendAfter: string | null;
}

export interface ChangeRow {
  readonly id: string;
  readonly visitId: string;
  readonly kind: VisitChangeKind;
  readonly fromDate: string | null;
  readonly toDate: string | null;
  readonly note: string | null;
  readonly toldAt: string | null;
  readonly silenceReason: SilenceReason | null;
  readonly inside24h: boolean;
  readonly chargeDecision: ChargeDecision;
  readonly changedBy: string | null;
  readonly createdAt: string;
  readonly intents: readonly MessageIntentRow[];
}

/** What a cleaner gets of one visit: the visit and who is on it with her. */
export interface CleanerVisitDetail {
  readonly visit: VisitRow;
  readonly cleaners: readonly CrewOnVisit[];
}

/** What the office gets: the same, plus the home, the household, the schedule and every change. */
export interface OfficeVisitDetail extends CleanerVisitDetail {
  readonly agreement: AgreementRow;
  readonly series: SeriesRow | null;
  readonly home: SiteRow;
  readonly client: ClientRow;
  readonly contacts: readonly ContactRow[];
  readonly changes: readonly ChangeRow[];
  /**
   * The visit's live invoice, the one a second draft is refused `already_invoiced` for and never a
   * voided one, or null while there is none (ops #61). The id alone: the invoice screen it opens is
   * where what is owed is shown, behind the recent sign-in. OPTIONAL BECAUSE A GRADUAL DEPLOYMENT
   * SPLITS CODE: absent offers "Bill it", which is what a Worker older than the field drew.
   */
  readonly invoiceId?: string | null;
  /**
   * The visit's reminders before the clean (ops #78), newest first, in a change's message shape. A
   * withdrawn one names the day it was for, and the visit may stand on another day now. OPTIONAL
   * BECAUSE A GRADUAL DEPLOYMENT SPLITS CODE, with the next field: absent draws no reminder line.
   */
  readonly reminders?: readonly ReminderRow[];
  /** What the page needs beside them to say where the next one stands, or why none went. */
  readonly reminder?: ReminderFacts;
  /**
   * A cleaner who cannot come (ops #85): the open call-off, or the latest closed one, or null. OPTIONAL
   * BECAUSE A GRADUAL DEPLOYMENT SPLITS CODE: absent draws no card.
   */
  readonly callOff?: CallOffView | null;
  /**
   * What they check first, each line photographed or not (ops #86 phase 4), or null where nothing is
   * typed. OPTIONAL BECAUSE A GRADUAL DEPLOYMENT SPLITS CODE: absent draws nothing.
   */
  readonly checksFirst?: ChecksFirstPhotographed | null;
}

/**
 * "N OF 3 PHOTOGRAPHED" (ops #86 phase 4, `api/src/records/photos.ts`): the household's own lines, from
 * the frozen sheet or from the home before the sheet is frozen, each with whether a live photo of the
 * visit stands for it, and whether the household allows photos now. Never a caption.
 */
export interface ChecksFirstPhotographed {
  readonly from: 'sheet' | 'home';
  readonly lines: readonly { readonly line: string; readonly photographed: boolean }[];
  readonly photographed: number;
  readonly photosAllowed: boolean;
}

/** A call-off as the visit page reads it (ops #85, `api/src/schedule/call-offs.ts`). */
export interface CallOffView {
  readonly id: string;
  readonly cleanerId: string;
  readonly cleanerName: string;
  readonly reason: CallOffReason;
  readonly heardAt: string;
  readonly reportedVia: CallOffReportedVia;
  readonly howEarly: string;
  readonly afterStart: boolean;
  readonly recordedBy: string;
  readonly recordedByName: string | null;
  readonly note: string | null;
  readonly taskId: string;
  readonly choice: CallOffChoice | null;
  readonly chosenAt: string | null;
  readonly visitChangeId: string | null;
  readonly substituteCleanerId: string | null;
  readonly substituteName: string | null;
  readonly createdAt: string;
}

/** A reminder before a clean (ops #78): an intent of the visit's own, about no change. */
export interface ReminderRow extends MessageIntentRow {
  readonly createdAt: string;
  /** The `scheduled_date` it names, from its outbox row. */
  readonly day: string | null;
  /** When the phone system reported it delivered, or null. */
  readonly deliveredAt: string | null;
  /**
   * Why a withdrawn one was withdrawn, or null for one that was not or whose reason the server does
   * not know. OPTIONAL BECAUSE A GRADUAL DEPLOYMENT SPLITS CODE: absent claims no reason.
   */
  readonly withdrawnFor?: ReminderWithdrawnFor | null;
  /**
   * WHAT HOME REPORTED OF ONE BY EMAIL (ops #76 O6), from its outbound row: when Microsoft accepted it,
   * when it failed and home's word for why, and a bounce after delivery; null on a text. OPTIONAL
   * BECAUSE A GRADUAL DEPLOYMENT SPLITS CODE.
   */
  readonly acceptedAt?: string | null;
  readonly failedAt?: string | null;
  readonly bouncedLateAt?: string | null;
  readonly homeSaid?: string | null;
}

/**
 * Why a reminder was withdrawn (ops #78): the visit changed, or her STOP to the phone system, the
 * office's do-not-contact mark, a stop in her own words, one held for the office, or her forget; and
 * for one by email (ops #76 O6), a stop or a mark landing on her or her address.
 */
export type ReminderWithdrawnFor =
  | 'visit_changed'
  | 'carrier_stop'
  | 'suppression'
  | 'forget'
  | 'stop_in_words'
  | 'stop_hold'
  | 'email_stop';

/** The facts of a visit's reminder the page cannot read for itself (ops #78, plan section 8). */
export interface ReminderFacts {
  /** When a reminder of the visit is due, under the owner's settings as they stand; null when off. */
  readonly moment: string | null;
  /** When the clean begins: no reminder goes after it. */
  readonly start: string;
  /** What the register says of the household's main contact by text; null with no main contact. */
  readonly register: {
    /** Her number ticked the website box, and no STOP, mark or hold stands. */
    readonly sms: boolean;
    readonly mark: boolean;
    readonly hold: boolean;
  } | null;
  /**
   * What the register says of the main contact by email (ops #76 O6): a yes the job's email goes on,
   * a do-not-contact mark on her or her address, a stop; null with no main contact. OPTIONAL BECAUSE A
   * GRADUAL DEPLOYMENT SPLITS CODE: absent draws no reminder line by email.
   */
  readonly emailRegister?: {
    readonly email: boolean;
    readonly mark: boolean;
    readonly stop: boolean;
  } | null;
  /** An ask from home about the visit is still undecided. */
  readonly askUndecided: boolean;
  /** The visit was written, or a change recorded on it, at or after its moment. */
  readonly changedSinceMoment: boolean;
}

export type VisitDetail = CleanerVisitDetail | OfficeVisitDetail;

/** The server decided which shape to send from the role it holds; this only reads the answer. */
export const isOfficeDetail = (detail: VisitDetail): detail is OfficeVisitDetail =>
  'agreement' in detail;

/** A contact with what the consent register says she may be told on. */
export interface ContactConsent extends ContactRow {
  readonly maySms: boolean;
  readonly mayEmail: boolean;
  readonly mayMessage: boolean;
  /**
   * What her choices would do to the change's message at the visit's home, on each channel (ops #76
   * phase 2). Absent from a Worker older than the field, which says nothing.
   */
  readonly choice?: ChoicePreview;
}

export interface MessagePreview {
  /** The default sentence. A person edits it; what she approves is what goes into the record. */
  readonly body: string;
  readonly contact: ContactRow | null;
  readonly channel: MessageChannel;
  /**
   * Whether the register would let this message go, for the contact and channel suggested. False
   * means the message path can only be refused, so the screen offers the typed reason instead of
   * a control that always fails after the sentence has been written.
   */
  readonly mayMessage: boolean;
  readonly contacts: readonly ContactConsent[];
}

/** What she agreed to, recorded where she said it. Append-only: a withdrawal is its own record. */
export interface ConsentCapture {
  readonly purposes: readonly ConsentPurpose[];
  readonly kind: ConsentKind;
  readonly captureMethod: CaptureMethod;
  /**
   * WHEN SHE SAID IT, which is not when it was typed. An instant at a seam is an instant and never
   * a sentence somebody typed, so this is an ISO string with an offset or nothing at all; omitted
   * means now, which is right while she is still on the phone. The server refuses one that has not
   * happened yet.
   */
  readonly capturedAt?: string | null;
  readonly evidenceText?: string | null;
}

export interface ChangeResult {
  readonly changeId: string;
  readonly intentId: string | null;
  /** True when the visit row has moved. False while the client has yet to be told. */
  readonly applied: boolean;
  /** Set when the message could not be sent because the client has not agreed to that channel. */
  readonly refusedByConsent: boolean;
  /**
   * The go-live switch's words when it refused the text (ops #31), and null otherwise. The change
   * is recorded either way and waits for a typed reason, as a refusal by the register does.
   */
  readonly gateRefusal: string | null;
  /**
   * Her choice's or the office's hold's words when they refused the message (ops #76 phase 2), led by
   * `not sent: `, and null otherwise; absent from a Worker older than the field.
   */
  readonly choiceRefusal?: string | null;
  /** Set where the office is given the call: neither email nor a text reaches the visit contact. */
  readonly officeCalls?: boolean;
}

export interface AgreementList {
  readonly agreements: readonly AgreementRow[];
  readonly series: readonly SeriesRow[];
}

export interface AgreementDetail {
  readonly agreement: AgreementRow;
  readonly series: readonly SeriesRow[];
  readonly visits: readonly VisitRow[];
  readonly home: SiteRow | null;
}

export interface MaterialiseSummary {
  readonly seriesConsidered: number;
  readonly visitsWritten: number;
  readonly assignmentsWritten: number;
  readonly slotsAlreadyFilled: number;
  readonly slotsClosed: number;
  readonly slotsPaused: number;
  readonly horizon: string;
}

export interface NewAgreementResult {
  readonly agreementId: string;
  readonly seriesId: string | null;
  readonly visitId: string | null;
  readonly materialised: MaterialiseSummary | null;
}

export interface CadenceChangeResult {
  readonly endedAgreementId: string;
  readonly agreementId: string;
  readonly seriesId: string;
  readonly visitsCancelled: number;
  readonly materialised: MaterialiseSummary;
}

export interface ClosedDateRow {
  readonly id: string;
  readonly closedOn: string;
  readonly kind: ClosedDateKind;
  readonly label: string;
}

/* ------------------------------------------------------------------ *
 * What goes in. Shaped to the zod schemas the routes validate with, so
 * a field this interface invents fails here rather than at the server.
 * ------------------------------------------------------------------ */

export interface NewContact {
  readonly role: ContactRole;
  readonly displayName: string;
  readonly phoneE164: string | null;
  readonly email: string | null;
  readonly preferredChannel: PreferredChannel | null;
  /**
   * What she agreed to as she was typed in, written in the same audited batch as the person it is
   * about, which is the only moment the two are certainly the same fact. Left out when nobody has
   * asked her: an unasked question is not a refusal, and the register has no edit path.
   */
  readonly consent?: ConsentCapture | null;
}

export interface NewSite {
  readonly label: string | null;
  readonly addressLine1: string;
  readonly addressLine2: string | null;
  readonly town: string;
  readonly postalCode: string;
  readonly county: County;
  readonly communityName: string | null;
  readonly bedrooms: number | null;
  readonly bathrooms: number | null;
  readonly squareFeet: number | null;
  readonly doNotList: string | null;
  readonly pets: string | null;
  readonly entryNotes: string | null;
  /** The three boxes joined by newlines (ops #86); the server trims and refuses past three lines. */
  readonly checksFirst?: string | null;
}

export interface NewClient {
  readonly displayName: string;
  /** Required, with no default. Priority 7: a client with no source cannot be counted. */
  readonly sourceChannel: Channel;
  readonly sourceDetail: string | null;
  /**
   * THE ENQUIRY THIS HOUSEHOLD IS. Sent when somebody typed her in by hand from a row that was
   * already on the enquiries list, so the two are one record rather than two: the office list
   * links her, report A counts her cohort, and the website is told what became of its lead. Left
   * out for a household nobody enquired about. The server refuses an enquiry a second household
   * has already claimed, and finishes the enquiry itself rather than leaving it half booked.
   */
  readonly enquiryId?: string | null;
  /** The household who introduced her, on a referral. The server refuses two introducers. */
  readonly referredByClientId?: string | null;
  readonly contact: NewContact;
  readonly site: NewSite;
}

/**
 * What a household's own record can be corrected to. Everything here is a fact about the household
 * rather than about its homes or its agreements, which have their own routes.
 */
export interface ClientPatch {
  readonly displayName?: string;
  readonly sourceChannel?: Channel;
  readonly sourceDetail?: string | null;
  readonly referredByClientId?: string | null;
  readonly referredByProspectId?: string | null;
  /** The language the household reads, set on the client's word (ops #98). */
  readonly language?: ClientLanguage;
}

export interface NewCleaner {
  readonly displayName: string;
  readonly phoneE164: string | null;
  readonly status: CleanerStatus;
  readonly startedOn: string | null;
}

export interface NewTerm {
  /** What one clean pays. Compensation, not a client price: no client price is ever typed. */
  readonly perCleanCents: number;
  readonly effectiveFrom: string;
}

/** The message a person approved. Composed by the server's default, edited here, sent by the phone system. */
export interface ChangeMessage {
  readonly contactId: string;
  readonly channel: Extract<MessageChannel, 'sms' | 'email'>;
  readonly body: string;
}

/**
 * The move flow. Exactly one of `silenceReason` and `message`, and the server refuses neither and
 * both: a change with no record of how she was told is the defect this system exists to prevent.
 */
export interface NewChange {
  readonly kind: VisitChangeKind;
  readonly toDate?: string | null;
  readonly windowStartLocal?: string | null;
  readonly windowEndLocal?: string | null;
  readonly cleanerId?: string | null;
  readonly note?: string | null;
  readonly chargeDecision?: ChargeDecision | null;
  readonly silenceReason?: SilenceReason | null;
  readonly message?: ChangeMessage | null;
  /** The call-off whose client's choice this change carries (ops #85). */
  readonly callOffId?: string | null;
}

export interface MessagePreviewParams {
  readonly kind: VisitChangeKind;
  readonly toDate?: string | null;
  readonly windowStartLocal?: string | null;
  readonly windowEndLocal?: string | null;
  readonly contactId?: string | null;
  /**
   * The channel the words are for, once somebody has chosen one: a text ends with the opt-out
   * line and an email does not (ops #23). Left out, the words are for her own way.
   */
  readonly channel?: 'sms' | 'email' | null;
}

export interface NewSeries {
  readonly anchorDate: string;
  readonly windowStartLocal: string;
  readonly windowEndLocal: string;
  readonly cleanerId?: string | null;
}

export interface NewOneOff {
  readonly date: string;
  readonly windowStartLocal: string;
  readonly windowEndLocal: string;
  readonly cleanerId?: string | null;
}

/**
 * A standing arrangement. No price is typed here: `rateLineCode` names a line of the published
 * card and the server reads the figure off it. `quotedPriceCents` is accepted ONLY for a line the
 * card publishes as quoted after a walkthrough, and refused for every other one.
 */
export interface NewAgreement {
  readonly siteId: string;
  readonly serviceLine: ServiceLine;
  readonly cadence: Cadence;
  readonly rateLineCode?: string | null;
  readonly quotedPriceCents?: number | null;
  readonly startedOn: string;
  readonly accessConfirmed?: boolean;
  readonly deepCleanDisclosed?: boolean;
  readonly series?: NewSeries | null;
  readonly oneOff?: NewOneOff | null;
}

export interface CadenceChange {
  readonly cadence: Cadence;
  /** The first day under the new cadence. The old agreement ends the day before. */
  readonly fromDate: string;
  readonly rateLineCode?: string | null;
  readonly quotedPriceCents?: number | null;
  readonly series: NewSeries;
  readonly silenceReason: SilenceReason;
}

export interface EndAgreement {
  readonly endedOn: string;
  readonly endedReason: Exclude<EndedReason, 'superseded_cadence_change'>;
  readonly silenceReason: SilenceReason;
  readonly note?: string | null;
}

export interface SeriesPatch {
  readonly windowStartLocal?: string;
  readonly windowEndLocal?: string;
  readonly cleanerId?: string | null;
}

export interface PauseSeries {
  readonly pausedFrom: string;
  readonly pausedUntil?: string | null;
  readonly holdSlotUntil?: string | null;
  readonly silenceReason: SilenceReason;
  readonly note?: string | null;
}

export interface NewClosedDate {
  readonly closedOn: string;
  readonly kind: ClosedDateKind;
  readonly label: string;
}

/* ------------------------------------------------------------------ *
 * The endpoints. GET /api/me, /api/clients, /api/cleaners, /api/catalog.
 * ------------------------------------------------------------------ */

export const getMe = (): Promise<Me> => request<Me>('/api/me');

export const listClients = (search: string): Promise<{ clients: ClientRow[] }> =>
  request(`/api/clients${search ? `?q=${encodeURIComponent(search)}` : ''}`);

export const getClient = (id: string): Promise<ClientRecord> =>
  request(`/api/clients/${encodeURIComponent(id)}`);

/** Household, first person and first home in one call, because the route creates them in one. */
export const createClient = (body: NewClient): Promise<{ id: string }> =>
  send('/api/clients', 'POST', body);

/**
 * Correcting the household's own record, audited like every other write. A patch carrying nothing
 * is refused by the server rather than quietly doing nothing, so a screen sends only what changed.
 */
/**
 * Stop charging the household's saved method, from the office, with why (ops #46, step 3b). 200 with
 * the row as it now stands, `stopped` false when it already stood stopped; 404 when nothing is saved.
 * The method comes off at Stripe after the answer.
 */
export const stopAutopay = (
  clientId: string,
  reason: OfficeRevokeReason,
): Promise<AutopayStopped> =>
  send(`/api/clients/${encodeURIComponent(clientId)}/autopay/stop`, 'POST', { reason });

export const patchClient = (id: string, body: ClientPatch): Promise<{ ok: true }> =>
  send(`/api/clients/${encodeURIComponent(id)}`, 'PATCH', body);

/** What stands in the way of forgetting a household, in the server's words (ops #82). */
export interface ForgetHold {
  readonly code: string;
  readonly message: string;
}

/**
 * WHETHER A HOUSEHOLD MAY BE FORGOTTEN NOW (ops #82): the first hold that stands, in its words, or
 * none. An owner's alone, as the door is; the page asks it only for an owner, and the server refuses
 * anybody else `403` whatever the page asks.
 */
export const getForgetHold = (clientId: string): Promise<{ hold: ForgetHold | null }> =>
  request(`/api/clients/${encodeURIComponent(clientId)}/forget`);

/**
 * FORGET THIS HOUSEHOLD (ops #82, `docs/plan-household-forget.md`): the household's name as the
 * owner typed it, which the server compares with the name the page shows. `403 step_up_required`
 * with a link when the owner has not signed in within a quarter of an hour; a hold answers `409` in
 * its words; a name typed wrong `400 confirmation_mismatch`. Answers what the phone system will be
 * asked about their texts, for the done sentence.
 */
export const forgetHousehold = (
  clientId: string,
  confirm: string,
): Promise<{
  ok: true;
  requestId: string;
  phoneSystem?: PhoneForgetOutcome;
  /** The other enquiries that share a number of theirs, which the forget left to review by hand. */
  sharing?: SharingEnquiries;
}> => send(`/api/clients/${encodeURIComponent(clientId)}/forget`, 'POST', { confirm });

/** How many other enquiries share a number of a household forgotten, and the first of them by id. */
export interface SharingEnquiries {
  readonly count: number;
  readonly ids: readonly string[];
}

/**
 * A PERSON'S OWN RECORD, EDITED (ops #76 item 5): only what changed, and at least one. A number, an
 * address or a way to reach her is cleared with a null; a name and a role never are. The server keeps
 * one primary a household (a person made primary demotes the one she replaces) and refuses the
 * primary made anything else, `is_primary`; a number another person on the household holds,
 * `phone_taken`; and a number changed under the box she ticked for texts, `texts_on_this_number`.
 */
export interface ContactEdit {
  readonly role?: ContactRole;
  readonly displayName?: string;
  readonly phoneE164?: string | null;
  readonly email?: string | null;
  readonly preferredChannel?: PreferredChannel | null;
}

export const editContact = (
  clientId: string,
  contactId: string,
  body: ContactEdit,
): Promise<{ ok: true }> =>
  send(
    `/api/clients/${encodeURIComponent(clientId)}/contacts/${encodeURIComponent(contactId)}`,
    'PATCH',
    body,
  );

/**
 * A PERSON TAKEN OFF THE HOUSEHOLD (ops #76 item 5): soft deleted, her door onto home ended with her
 * asks still waiting, never the primary (`is_primary`) and never the last person (`last_contact`).
 * `doorEnded` says whether she had a door onto home.
 */
export const removeContact = (
  clientId: string,
  contactId: string,
): Promise<{ ok: true; doorEnded: boolean }> =>
  request(
    `/api/clients/${encodeURIComponent(clientId)}/contacts/${encodeURIComponent(contactId)}`,
    { method: 'DELETE' },
  );

/**
 * ONE PERSON'S DOOR ONTO HOME (ops #19, step 5): the office's decision that she may see this
 * household through the client system, and where it stands. Nothing of home's sign-in is here, and
 * nothing is a credential: ops holds her address already, and home proves it.
 */
export interface MembershipRow {
  readonly id: string;
  readonly clientId: string;
  readonly contactId: string;
  readonly role: MembershipRole;
  /** The homes a member may see, or null for every home of the household. */
  readonly homes: readonly string[] | null;
  readonly state: MembershipState;
  readonly invitedAt: string;
  readonly boundAt: string | null;
  readonly revokedAt: string | null;
  readonly revokeReason: MembershipRevokeReason | null;
}

export const listMemberships = (clientId: string): Promise<{ memberships: MembershipRow[] }> =>
  request(`/api/clients/${encodeURIComponent(clientId)}/memberships`);

/** "Invite to home": the row written `invited`, and home emails her the invitation. */
export const inviteToHome = (
  clientId: string,
  body: { contactId: string; role: MembershipRole; homes: readonly string[] | null },
): Promise<MembershipRow> =>
  send(`/api/clients/${encodeURIComponent(clientId)}/memberships`, 'POST', body);

/** "Remove from home": the door revoked by the office's decision, and her open asks with it. */
export const removeFromHome = (
  clientId: string,
  membershipId: string,
): Promise<{ membership: MembershipRow; revoked: boolean }> =>
  send(
    `/api/clients/${encodeURIComponent(clientId)}/memberships/${encodeURIComponent(membershipId)}`,
    'DELETE',
    { reason: 'office_decision' },
  );

/** "Send it all again": the household's whole copy enqueued for home afresh. */
export const resendToHome = (clientId: string): Promise<{ enqueued: number }> =>
  send(`/api/clients/${encodeURIComponent(clientId)}/memberships/resend`, 'POST', {});

/* ------------------------------------------------------------------ *
 * What each person is sent, and the office's holds (ops #76 phase 2,
 * phase 4 of `docs/plan-preferences.md`, `api/src/routes/preferences.ts`).
 * ------------------------------------------------------------------ */

/** A period the office holds a kind for. */
export interface HeldPeriod {
  readonly from: string;
  readonly until: string | null;
}

/** One row of what a person is sent: one category at every home, or at one home of theirs. */
export interface ChoiceSetting {
  readonly homeId: string | null;
  readonly category: PreferenceCategory | 'account';
  readonly email: 'on' | 'off';
  /** Whether that kind reaches them by text too, the register's and the texting switch's answer. */
  readonly text: boolean;
  readonly locked: Lock | null;
  readonly leadDays: number | null;
  readonly sendTime: SendTime | null;
  readonly heldByOffice: HeldPeriod | null;
  readonly changed: {
    readonly at: string;
    readonly by: 'self' | 'office' | 'system';
    readonly how: PreferenceHow;
  } | null;
  /** The guard: the visit or money contact with no way open to them, so the office calls. */
  readonly officeCalls: boolean;
}

/** One person's choices, as the office's read answers them. */
export interface PersonChoicesView {
  readonly personId: string;
  readonly door: {
    readonly role: MembershipRole;
    readonly state: string;
    readonly homes: readonly string[] | null;
  } | null;
  readonly email: { readonly onFile: boolean; readonly state: EmailState };
  readonly texts: {
    readonly onFile: boolean;
    readonly state: TextsState;
    readonly turnOnBy: TurnOnBy | null;
  };
  /** Whether a text of a texted kind would reach them now: the register and the switch. */
  readonly textsReach: boolean;
  /** The household's live homes they may see. */
  readonly homesOffered: readonly string[];
  /** The categories offered to them, `account` aside. */
  readonly offered: readonly PreferenceCategory[];
  readonly visitsGoTo: string | null;
  readonly moneyGoesTo: string | null;
  readonly settings: readonly ChoiceSetting[];
  /**
   * Their pause that stands, covering today or the next to begin, or null (phase 5). Absent from a
   * Worker older than phase 5, which the card draws as no pause and offers no change of.
   */
  readonly paused?: MessagePause | null;
  /** The hours a text may reach them, or null for whenever the texting hours allow (phase 5). */
  readonly quietHours?: QuietHours | null;
}

/** A pause of theirs: from a day, until a day or until ended, at one home or every home. */
export interface MessagePause {
  readonly from: string;
  readonly until: string | null;
  readonly homeId: string | null;
}

/** The hours a text may reach them, `HH:MM` in Florida, inside 08:00 to 20:00. */
export interface QuietHours {
  readonly from: string;
  readonly until: string;
}

/** A hold of the office's that stands, with its Lift. */
export interface MessageHold {
  readonly id: string;
  readonly personId: string | null;
  readonly homeId: string | null;
  readonly category: PreferenceCategory | null;
  readonly from: string;
  readonly until: string | null;
  /** In force today, rather than waiting for its first day. */
  readonly covering: boolean;
  readonly how: PreferenceHow;
  readonly note: string | null;
  readonly changedAt: string;
  readonly changedByName: string | null;
}

/** What the office's read of a household's choices answers. */
export interface HouseholdChoices {
  readonly today: string;
  readonly visitsGoTo: string | null;
  readonly moneyGoesTo: string | null;
  readonly people: readonly PersonChoicesView[];
  readonly holds: readonly MessageHold[];
}

/** A choice the office writes on their word. */
export interface ChoiceChange {
  readonly kind: 'choice';
  readonly category: PreferenceCategory;
  readonly homeId: string | null;
  readonly email: 'on' | 'off';
  readonly leadDays?: number | null;
  readonly sendTime?: SendTime | null;
  readonly how: OfficePreferenceHow;
  readonly reason: string;
  /** The office will call them, where this leaves them no way to be reached. */
  readonly officeCalls?: boolean;
}

/** Their pause on their word, or the one that stands ended (null). */
export interface PauseChange {
  readonly kind: 'pause';
  readonly pause: MessagePause | null;
  readonly how: OfficePreferenceHow;
  readonly reason: string;
}

/** Their quiet hours on their word, or taken off (null). */
export interface QuietChange {
  readonly kind: 'quiet';
  readonly quietHours: QuietHours | null;
  readonly how: OfficePreferenceHow;
  readonly reason: string;
}

/** A hold the office sets. */
export interface HoldChange {
  readonly personId: string | null;
  readonly homeId: string | null;
  readonly category: PreferenceCategory | null;
  readonly from: string;
  readonly until: string | null;
  /** How they asked for it, or null where the office decided it. */
  readonly how: OfficePreferenceHow | null;
  readonly reason: string;
}

/** What every write of the office's answers. */
export interface ChoiceWritten {
  readonly id: string;
  /** Where the office will be given the call for what this parks. */
  readonly officeCalls: boolean;
}

export const getHouseholdChoices = (clientId: string): Promise<HouseholdChoices> =>
  request(`/api/clients/${encodeURIComponent(clientId)}/preferences`);

export const setChoice = (
  clientId: string,
  contactId: string,
  body: ChoiceChange | PauseChange | QuietChange,
): Promise<ChoiceWritten> =>
  send(
    `/api/clients/${encodeURIComponent(clientId)}/contacts/${encodeURIComponent(contactId)}/preferences`,
    'POST',
    body,
  );

export const setHold = (clientId: string, body: HoldChange): Promise<ChoiceWritten> =>
  send(`/api/clients/${encodeURIComponent(clientId)}/holds`, 'POST', body);

export const liftHold = (
  clientId: string,
  holdId: string,
  body: { readonly how: OfficePreferenceHow | null; readonly reason: string },
): Promise<ChoiceWritten> =>
  send(
    `/api/clients/${encodeURIComponent(clientId)}/holds/${encodeURIComponent(holdId)}/lift`,
    'POST',
    body,
  );

export const listCleaners = (): Promise<{ cleaners: CleanerRow[] }> => request('/api/cleaners');

export const createCleaner = (body: NewCleaner): Promise<{ id: string }> =>
  send('/api/cleaners', 'POST', body);

/** Owners only, and the server is the one enforcing that. See `api/src/routes/cleaners.ts`. */
export const listTerms = (cleanerId: string): Promise<{ terms: TermRow[] }> =>
  request(`/api/cleaners/${encodeURIComponent(cleanerId)}/terms`);

export const addTerm = (cleanerId: string, body: NewTerm): Promise<{ id: string }> =>
  send(`/api/cleaners/${encodeURIComponent(cleanerId)}/terms`, 'POST', body);

/** One cleaner, and for the office the count of their call-offs (ops #85). */
export const getCleaner = (cleanerId: string): Promise<CleanerDetail> =>
  request(`/api/cleaners/${encodeURIComponent(cleanerId)}`);

/** The office's edit of one cleaner's row; here, the make-up slot (ops #85). */
export const patchCleaner = (
  cleanerId: string,
  body: {
    readonly makeUpWeekday?: number | null;
    readonly makeUpWindowStartLocal?: string | null;
    readonly makeUpWindowEndLocal?: string | null;
  },
): Promise<{ ok: true }> => send(`/api/cleaners/${encodeURIComponent(cleanerId)}`, 'PATCH', body);

/** Owners only: the day the office's own checks were complete, or null to clear it (ops #85). */
export const setCleanerChecks = (
  cleanerId: string,
  completeOn: string | null,
): Promise<{ ok: true }> =>
  send(`/api/cleaners/${encodeURIComponent(cleanerId)}/checks`, 'PUT', { completeOn });

/**
 * Owners only: the cleaner's lane, payroll or fill-in on a 1099, with the day it took effect, or
 * null for both to clear it (ops #87, `docs/plan-two-lanes.md` section 3).
 */
export const setCleanerLane = (
  cleanerId: string,
  classification: Classification | null,
  changedOn: string | null,
): Promise<{ ok: true }> =>
  send(`/api/cleaners/${encodeURIComponent(cleanerId)}/lane`, 'PUT', {
    classification,
    changedOn,
  });

/** What the office heard from a cleaner who cannot come to one visit (ops #85). */
export interface NewCallOff {
  readonly cleanerId: string;
  readonly reason: CallOffReason;
  readonly reportedVia: Exclude<CallOffReportedVia, 'app'>;
  readonly heardAt?: string | null;
  readonly note?: string | null;
}

/** The call-off, its follow-up and the owners' notices, in one batch; the visit does not move. */
export const recordCallOff = (
  visitId: string,
  body: NewCallOff,
): Promise<{ id: string; inLast60Days: number; afterStart: boolean }> =>
  send(`/api/visits/${encodeURIComponent(visitId)}/call-off`, 'POST', body);

/** A call-off made in error, withdrawn while no choice stands. */
export const withdrawCallOff = (callOffId: string): Promise<{ ok: true }> =>
  request(`/api/call-offs/${encodeURIComponent(callOffId)}`, { method: 'DELETE' });

/** Read only, everywhere, forever. The published page is the contract and this is its copy. */
export const getCatalog = (): Promise<Catalog> => request('/api/catalog');

/* ------------------------------------------------------------------ *
 * The schedule. /api/schedule, /api/visits, /api/changes.
 * ------------------------------------------------------------------ */

/** The office Today screen: every visit between two days with its home, household and crew. */
export const getSchedule = (from: string, to: string): Promise<Schedule> =>
  request(`/api/schedule${query({ from, to })}`);

/** Visits as the scoped reader allows: hers for a cleaner, everybody's for the office. */
export const listVisits = (from: string, to: string): Promise<VisitList> =>
  request(`/api/visits${query({ from, to })}`);

export const getVisit = (id: string): Promise<VisitDetail> =>
  request(`/api/visits/${encodeURIComponent(id)}`);

/**
 * What she agreed to, written down. Append-only in the register, so this records and never edits:
 * a withdrawal is a second call with `kind` withdrawn, and the position in force is the last one.
 */
export const recordConsent = (
  clientId: string,
  contactId: string,
  capture: ConsentCapture,
): Promise<{ recorded: number }> =>
  send(
    `/api/clients/${encodeURIComponent(clientId)}/contacts/${encodeURIComponent(contactId)}/consent`,
    'POST',
    capture,
  );

/** The default sentence for a change, before anybody decides to send it. Nothing is written. */
export const previewMessage = (id: string, params: MessagePreviewParams): Promise<MessagePreview> =>
  request(
    `/api/visits/${encodeURIComponent(id)}/message${query({
      kind: params.kind,
      toDate: params.toDate,
      windowStartLocal: params.windowStartLocal,
      windowEndLocal: params.windowEndLocal,
      contactId: params.contactId,
      channel: params.channel,
    })}`,
  );

/** The move flow. The change record is written first; the calendar changes last. */
export const recordChange = (visitId: string, body: NewChange): Promise<ChangeResult> =>
  send(`/api/visits/${encodeURIComponent(visitId)}/changes`, 'POST', body);

/** She was told another way, or cannot be told. Types the reason and lets the visit move. */
export const silenceChange = (
  changeId: string,
  silenceReason: SilenceReason,
): Promise<{ ok: true; applied: boolean }> =>
  send(`/api/changes/${encodeURIComponent(changeId)}/silence`, 'POST', { silenceReason });

/** Whether a late cancellation is charged. A human decision, made after the fact. */
export const decideCharge = (
  changeId: string,
  chargeDecision: ChargeDecision,
): Promise<{ ok: true }> =>
  send(`/api/changes/${encodeURIComponent(changeId)}/charge`, 'POST', { chargeDecision });

/* ------------------------------------------------------------------ *
 * Agreements and the standing schedule. /api/agreements, /api/series.
 * ------------------------------------------------------------------ */

export const listAgreements = (siteId: string): Promise<AgreementList> =>
  request(`/api/agreements${query({ siteId })}`);

export const getAgreement = (id: string): Promise<AgreementDetail> =>
  request(`/api/agreements/${encodeURIComponent(id)}`);

export const createAgreement = (body: NewAgreement): Promise<NewAgreementResult> =>
  send('/api/agreements', 'POST', body);

/** Ends this agreement and opens another, because cadence sets the price. */
export const changeCadence = (id: string, body: CadenceChange): Promise<CadenceChangeResult> =>
  send(`/api/agreements/${encodeURIComponent(id)}/cadence`, 'POST', body);

export const endAgreement = (
  id: string,
  body: EndAgreement,
): Promise<{ ok: true; visitsCancelled: number }> =>
  send(`/api/agreements/${encodeURIComponent(id)}/end`, 'POST', body);

/** The default for visits not yet written. A visit already on the calendar moves through the move flow. */
export const patchSeries = (id: string, body: SeriesPatch): Promise<{ ok: true }> =>
  send(`/api/series/${encodeURIComponent(id)}`, 'PATCH', body);

export const pauseSeries = (
  id: string,
  body: PauseSeries,
): Promise<{ ok: true; visitsCancelled: number }> =>
  send(`/api/series/${encodeURIComponent(id)}/pause`, 'POST', body);

export interface ResumeSeries {
  readonly resumeOn: string;
  /** Needed only when she comes back later than the pause was set to end. */
  readonly silenceReason?: SilenceReason | null;
}

export const resumeSeries = (
  id: string,
  body: ResumeSeries,
): Promise<{ ok: true; materialised: MaterialiseSummary & { visitsCancelled: number } }> =>
  send(`/api/series/${encodeURIComponent(id)}/resume`, 'POST', body);

/**
 * A STANDING SCHEDULE MOVED TO ANOTHER WEEKDAY AT THE SAME CADENCE (ops #76): `fromDate` is the first
 * day the old weekday no longer holds, `anchorDate` the first clean on the new one, on or after it.
 * Every open clean from `fromDate` comes off the calendar with its record and the reason typed, and a
 * paused schedule is moved with its pause. `cleanerId` left out keeps whose slot it was.
 */
export interface Reanchor {
  readonly fromDate: string;
  readonly anchorDate: string;
  readonly windowStartLocal: string;
  readonly windowEndLocal: string;
  readonly cleanerId?: string | null;
  readonly silenceReason: SilenceReason;
  readonly note?: string | null;
}

export interface ReanchorResult {
  readonly endedSeriesId: string;
  readonly seriesId: string;
  readonly visitsCancelled: number;
  readonly materialised: MaterialiseSummary;
}

export const reanchorSeries = (id: string, body: Reanchor): Promise<ReanchorResult> =>
  send(`/api/series/${encodeURIComponent(id)}/reanchor`, 'POST', body);

/* ------------------------------------------------------------------ *
 * Days we are closed. /api/closed-dates.
 * ------------------------------------------------------------------ */

export const listClosedDates = (): Promise<{ closedDates: ClosedDateRow[] }> =>
  request('/api/closed-dates');

export const addClosedDate = (body: NewClosedDate): Promise<{ id: string }> =>
  send('/api/closed-dates', 'POST', body);

export const removeClosedDate = (id: string): Promise<{ ok: true }> =>
  request(`/api/closed-dates/${encodeURIComponent(id)}`, { method: 'DELETE' });

/* ------------------------------------------------------------------ *
 * The job sheet. /api/visits/:id/sheet, start, finish, no-access.
 * ------------------------------------------------------------------ */

/**
 * The sheet as it was frozen the first time anybody opened it. It carries no price by schema
 * invariant, and no code: `secrets` below lists which codes exist, by label, and each value is a
 * separate audited read inside the visit window.
 */
export interface SheetRow {
  readonly id: string;
  readonly visitId: string;
  readonly scopeVersion: string;
  readonly frozenAt: string;
  readonly frozenBy: string | null;
  /** The address as one block of text, lines separated by newlines. */
  readonly addressText: string;
  readonly windowStart: string | null;
  readonly windowEnd: string | null;
  readonly doNotList: string | null;
  readonly pets: string | null;
  readonly entryInstructions: string | null;
  /** What the client asked for last time, carried forward so it survives a change of cleaner. */
  readonly clientAskedFor: string | null;
  /**
   * What they check first, as the home said it at the first open (ops #86). Optional because a sheet
   * served by a Worker before migration `0047` carries no such field.
   */
  readonly checksFirst?: string | null;
}

export interface ScopeOnSheet {
  readonly id: string;
  readonly version: number;
  readonly body: string;
  readonly contentHash: string;
}

/** A code that exists for this home. The label is what she matches to the lock; the value is not here. */
export interface SecretLabel {
  readonly id: string;
  readonly kind: AccessSecretKind;
  readonly label: string | null;
}

/** One list of the company checklist on a sheet: a heading and its items, each a stable id. */
export interface ChecklistSection {
  readonly id: string;
  readonly title: string;
  readonly items: readonly { readonly id: string; readonly words: string }[];
}

/**
 * THE COMPANY CHECKLIST ON A SHEET (ops #87, `docs/plan-two-lanes.md` section 7): the business's lists
 * that apply to this visit, by version, where the reader is on the crew in the payroll lane for its
 * day, or is the office and somebody on the crew is. It carries no word of a lane.
 */
export interface SheetChecklist {
  readonly version: string;
  readonly readOn: string;
  readonly sections: readonly ChecklistSection[];
  /** The paper's note of what is never done on any visit, as words, never ticked. */
  readonly never: string;
  /** What the visit's report has ticked, by id; an id no section has was ticked on an earlier list. */
  readonly ticks: readonly string[];
  /** True once the report is submitted: the marks stand, read only. */
  readonly ticksFixed: boolean;
  /** The office's alone: the cleaners on the crew the lists apply to. */
  readonly appliesTo?: readonly string[];
}

export interface SheetView {
  readonly visit: VisitRow;
  readonly sheet: SheetRow;
  readonly scope: ScopeOnSheet | null;
  readonly crew: readonly { readonly cleanerId: string; readonly displayName: string }[];
  readonly secrets: readonly SecretLabel[];
  /** True from twelve hours before the window to four hours after it. Codes open only then. */
  readonly windowOpen: boolean;
  readonly frozenNow: boolean;
  /**
   * The company checklist, or null where it does not apply to the reader (ops #87). Optional because a
   * Worker before it answers no such key, which draws nothing.
   */
  readonly checklist?: SheetChecklist | null;
  /**
   * The household's plan for the home, the version this sheet froze, as written and never translated,
   * or null (ops #74 item 6). Optional because a Worker before it answers no such key.
   */
  readonly homePlan?: {
    readonly id: string;
    readonly version: number;
    readonly body: string;
  } | null;
  /**
   * The keys held for this home (ops #74 item 6): a cleaner's own; for the office every holder's, each
   * with the holder's id and name. Optional because a Worker before it answers no such key.
   */
  readonly keysHeld?: readonly {
    readonly keyKind: string;
    readonly keyLabel: string;
    readonly since: string;
    readonly cleanerId?: string;
    readonly displayName?: string;
  }[];
}

export const getSheet = (visitId: string): Promise<SheetView> =>
  request(`/api/visits/${encodeURIComponent(visitId)}/sheet`);

/** Stamps the start. Nothing else is captured: no position, no photo, no device. */
export const startVisit = (visitId: string): Promise<{ ok: true; startedAt: string }> =>
  send(`/api/visits/${encodeURIComponent(visitId)}/start`, 'POST', {});

export const finishVisit = (visitId: string): Promise<{ ok: true; finishedAt: string }> =>
  send(`/api/visits/${encodeURIComponent(visitId)}/finish`, 'POST', {});

/** She could not get in. Every active owner is told at once. */
export const reportNoAccess = (
  visitId: string,
  note: string | null,
): Promise<{ ok: true; ownersTold: number }> =>
  send(`/api/visits/${encodeURIComponent(visitId)}/no-access`, 'POST', { note });

/* ------------------------------------------------------------------ *
 * The scope a job sheet freezes. /api/scopes. Office only.
 * ------------------------------------------------------------------ */

/**
 * ONE VERSION OF THE WORDS A CLEAN IS JUDGED AGAINST. The sheet freezes a pointer to one of these
 * and refuses to open when none is approved and in force, which is right: the published policy
 * offers a free re-clean when something was missed, and "missed" means anything only against a
 * written list.
 *
 * TWO ACTS, NOT ONE. Publishing writes the words unapproved; approving puts a person's name
 * against them. A draft is visible to the office and invisible to every door.
 */
export interface ScopeRow {
  readonly id: string;
  readonly serviceLine: ServiceLine;
  /** Null is the standard scope for the line. A home makes it that home's tailored version. */
  readonly siteId: string | null;
  readonly version: number;
  readonly body: string;
  readonly contentHash: string;
  /** Where the client read it. Set for us when the body is the text the business publishes. */
  readonly sourceUrl: string | null;
  readonly effectiveFrom: string;
  readonly effectiveTo: string | null;
  readonly approvedAt: string | null;
  readonly approvedBy: string | null;
}

/** The words as the business publishes them today, which a new version takes by default. */
export interface PublishedScope {
  readonly serviceLine: ServiceLine;
  readonly body: string;
}

export interface ScopeList {
  readonly scopes: readonly ScopeRow[];
  readonly published: readonly PublishedScope[];
}

/** `body` omitted takes the published text for the line, which is the usual case. */
export interface NewScope {
  readonly serviceLine: ServiceLine;
  readonly body?: string | null;
  readonly siteId?: string | null;
  readonly effectiveFrom?: string | null;
  readonly sourceUrl?: string | null;
}

export const listScopes = (): Promise<ScopeList> => request('/api/scopes');

export const publishScope = (body: NewScope): Promise<ScopeRow> =>
  send('/api/scopes', 'POST', body);

/** A name against the words. The server refuses a second approval on the same version. */
export const approveScope = (id: string): Promise<ScopeRow> =>
  send(`/api/scopes/${encodeURIComponent(id)}/approve`, 'POST', {});

/**
 * THE DAY THESE WORDS STOP, which the table could not say until now.
 *
 * `effectiveTo` is exclusive, exactly as `isInForce` in `web/src/scopes.ts` and `scopeFor` in
 * `api/src/jobsheet/sheet.ts` read it, so the day sent is the first one the version no longer
 * adjudicates and every sheet frozen before it quotes what it always quoted. The screen sends it
 * rather than letting the server pick, because which day a document stops applying is the office's
 * decision and not a default.
 */
export const retireScope = (id: string, effectiveTo: string): Promise<ScopeRow> =>
  send(`/api/scopes/${encodeURIComponent(id)}/retire`, 'POST', { effectiveTo });

/* ------------------------------------------------------------------ *
 * Settings. /api/settings (ops #46, step 2b): the clocks "How did we
 * do?" keeps and the business hours, and since ops #31 texting's
 * switches. The office reads; an owner sets.
 * ------------------------------------------------------------------ */

/**
 * How the screen draws one: a count of hours, a time of day, days of the week, one of a few words
 * (`choices`), a day with how we know it, phone numbers, email addresses drawn as the numbers are
 * (`email_test_addresses`, ops #76 O6), a person in the office, whose value is her user id and whose
 * control is a picker of names (`text_inbox_owner`, ops #25), or a few lines of words, one a line
 * break, drawn as a small box (`document_address`, ops #76 O3), a build of the cleaners' app as three
 * whole numbers, or one line of words (`cleaner_app.minimum`, `.latest` and `.message`, ops #92).
 */
export type SettingKind =
  | 'hours'
  | 'time'
  | 'days'
  | 'switch'
  | 'date'
  | 'numbers'
  | 'addresses'
  | 'user'
  | 'lines'
  | 'version'
  | 'words';

export interface SettingRow {
  readonly key: string;
  readonly kind: SettingKind;
  readonly label: string;
  readonly hint: string;
  /** The words a switch accepts, in the order to offer them; absent for every other kind. */
  readonly choices?: readonly string[];
  /** What an owner set, or null where the default stands. */
  readonly value: string | null;
  readonly default: string;
  readonly updatedAt: string | null;
  readonly updatedByName: string | null;
}

export const listSettings = (): Promise<{ settings: SettingRow[] }> => request('/api/settings');

/** An owner's alone; the server refuses anybody else and says why a value will not do. */
export const putSetting = (key: string, value: string): Promise<{ setting: SettingRow }> =>
  send(`/api/settings/${encodeURIComponent(key)}`, 'PUT', { value });

/**
 * Back to the default (ops #54, item 8): the owner's value is withdrawn and the key reads its default
 * again. An owner's alone, and refused in the server's words where the default would break a pair.
 */
export const resetSetting = (key: string): Promise<{ setting: SettingRow }> =>
  request(`/api/settings/${encodeURIComponent(key)}`, { method: 'DELETE' });

/**
 * RE-GRANTING TEXTING FROM THE WEBSITE TICK (ops #55, item 5): an owner's, and the server refuses
 * anybody else. The count first, which writes nothing: how many a press would grant from their own
 * household's tick, and how many it passes by, by reason. Then the press, which grants at most
 * `perPress` and says how many remain for another press. Nothing is sent to anybody.
 */
export interface TextingRegrantCount {
  readonly toGrant: number;
  readonly skipped: Readonly<Record<TextingRegrantSkip, number>>;
  readonly perPress: number;
}

export interface TextingRegrantResult {
  readonly granted: number;
  readonly remaining: number;
  readonly skipped: Readonly<Record<TextingRegrantSkip, number>>;
  readonly perPress: number;
}

export const countTextingRegrant = (): Promise<TextingRegrantCount> =>
  request('/api/settings/texting-regrant');

export const regrantTexting = (): Promise<TextingRegrantResult> =>
  send('/api/settings/texting-regrant', 'POST', {});

/* ------------------------------------------------------------------ *
 * Codes. /api/secrets. Labels in lists, values only from a reveal.
 * ------------------------------------------------------------------ */

export interface SecretRow {
  readonly id: string;
  readonly siteId: string;
  readonly kind: AccessSecretKind;
  readonly label: string | null;
  readonly sensitivity: Sensitivity;
  /** Set when somebody who saw this code has left. Cleared when the code changes. */
  readonly rotationDueAt: string | null;
  readonly createdAt: string;
  /**
   * Why its kind is no longer kept, where it is retired (an alarm code, since 2026-10-24), or null.
   * Such a row is kept from before and is there to be removed; the server refuses a new value for it.
   */
  readonly retired: string | null;
}

/** The one shape that carries a value. It lives in component state for a minute and nowhere else. */
export interface RevealedSecret {
  readonly id: string;
  readonly kind: AccessSecretKind;
  readonly label: string | null;
  readonly value: string;
}

export interface NewSecret {
  readonly siteId: string;
  readonly kind: WritableAccessSecretKind;
  readonly label: string;
  readonly sensitivity?: Sensitivity;
  readonly value: string;
}

export const listSecrets = (siteId: string): Promise<{ secrets: SecretRow[] }> =>
  request(`/api/secrets${query({ siteId })}`);

export const createSecret = (body: NewSecret): Promise<{ id: string; sensitivity: Sensitivity }> =>
  send('/api/secrets', 'POST', body);

/**
 * A POST because it is an act: the server writes the audit row before it decrypts anything. The
 * return path rides along so a step-up refusal knows where to come back to.
 */
export const revealSecret = (id: string, returnPath: string): Promise<RevealedSecret> =>
  send(`/api/secrets/${encodeURIComponent(id)}/reveal${query({ return: returnPath })}`, 'POST', {});

export const rotateSecret = (id: string, value: string): Promise<{ ok: true }> =>
  send(`/api/secrets/${encodeURIComponent(id)}/rotate`, 'POST', { value });

export const markRotationDue = (id: string): Promise<{ ok: true }> =>
  send(`/api/secrets/${encodeURIComponent(id)}/rotation-due`, 'POST', {});

export const removeSecret = (id: string): Promise<{ ok: true }> =>
  request(`/api/secrets/${encodeURIComponent(id)}`, { method: 'DELETE' });

/* ------------------------------------------------------------------ *
 * Records. /api/visits/:id/report, /api/visits/:id/issues, /api/issues.
 * ------------------------------------------------------------------ */

export interface ReportRow {
  readonly id: string;
  readonly visitId: string;
  readonly submittedAt: string | null;
  readonly submittedBy: string | null;
  readonly whatWasDone: string;
  readonly clientAskedFor: string | null;
  /** For the office only. A report can reach the client through a link; this column never does. */
  readonly notesForOwner: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
  /**
   * The company checklist's ticks (ops #87), for a reader the checklist applies to and the office;
   * absent for anybody else, and from a Worker before them.
   */
  readonly checklistVersion?: string | null;
  readonly checklistTicks?: readonly string[] | null;
  /**
   * WHAT THE CLIENT READS (ops #98, `docs/plan-client-language.md` section 4): the office's own words
   * beside the cleaner's, in the office's answer alone; absent for a cleaner, and from a Worker before
   * them. Null until the office writes them.
   */
  readonly forClientWhatWasDone?: string | null;
  readonly forClientAskedFor?: string | null;
  readonly forClientLanguage?: ClientLanguage | null;
  readonly forClientBy?: string | null;
  readonly forClientAt?: string | null;
}

/** The report's read: the row, and for the office the language the household reads now (ops #98). */
export interface ReportRead {
  readonly report: ReportRow | null;
  readonly householdLanguage?: ClientLanguage | null;
}

/** The office's words for the client about a submitted report (ops #98). */
export interface ForClientInput {
  readonly whatWasDone: string;
  readonly askedFor: string | null;
}

export interface ReportInput {
  readonly whatWasDone: string;
  readonly clientAskedFor: string | null;
  readonly notesForOwner: string | null;
  /**
   * The ticks, saved with the words in this one save and never alone (ops #87): absent keeps the ticks
   * that stand. Sent only where the sheet carries the checklist.
   */
  readonly checklistVersion?: string;
  readonly checklistTicks?: readonly string[];
}

export interface IssueRow {
  readonly id: string;
  readonly visitId: string;
  readonly kind: IssueKind;
  readonly state: IssueState;
  readonly discoveredByClient: boolean;
  readonly reportedAt: string;
  readonly reportedBy: string | null;
  readonly clientToldAt: string | null;
  readonly summary: string;
  readonly detail: string | null;
  readonly adjudicatedAgainstScopeVersion: string | null;
  readonly remedy: Remedy | null;
  readonly remedyCostCents: number | null;
  readonly acknowledgedAt: string | null;
  readonly remediedAt: string | null;
  readonly closedAt: string | null;
}

/** The office row: the issue joined to its visit, home and household. */
export interface OfficeIssueRow extends IssueRow {
  /**
   * WHAT THE CLIENT READS OF THE ISSUE (ops #98, `docs/plan-client-language.md` sections 4 and 7): the
   * office's own line beside the summary, its language, who and when, null until written, and the
   * Already there mark, null until the office decides. In the office's answer alone; absent from a
   * Worker before them.
   */
  readonly forClientSummary?: string | null;
  readonly forClientLanguage?: ClientLanguage | null;
  readonly forClientBy?: string | null;
  readonly forClientAt?: string | null;
  readonly alreadyThere?: boolean | null;
  /** The language the household reads now (ops #98); absent from a Worker before it. */
  readonly householdLanguage?: ClientLanguage;
  readonly visitDate: string;
  readonly siteId: string;
  readonly addressLine1: string;
  readonly clientId: string;
  readonly clientName: string;
}

/** The office's line for the client about an issue (ops #98); the mark only on kind `other`. */
export interface IssueForClientInput {
  readonly summary: string;
  readonly alreadyThere?: boolean;
}

export interface NewIssue {
  readonly kind: IssueKind;
  readonly summary: string;
  readonly detail: string | null;
  readonly discoveredByClient: boolean;
}

export interface IssuePatch {
  readonly state?: IssueState;
  /** "I told the client." The server stamps when. */
  readonly clientTold?: boolean;
  readonly remedy?: Remedy | null;
  readonly remedyCostCents?: number | null;
  readonly detail?: string | null;
}

export const getReport = (visitId: string): Promise<ReportRead> =>
  request(`/api/visits/${encodeURIComponent(visitId)}/report`);

/**
 * The office writes what the client reads of a submitted report, in its own words (ops #98). Only
 * then does the household's journal at home carry the visit; a second save replaces the first.
 */
export const writeReportForClient = (
  visitId: string,
  input: ForClientInput,
): Promise<{ ok: true; forClientAt: string }> =>
  send(`/api/visits/${encodeURIComponent(visitId)}/report/for-client`, 'PUT', input);

/** Create or edit the draft. One report per visit; a second save edits the first. */
export const saveReport = (
  visitId: string,
  body: ReportInput,
): Promise<{ id: string; created: boolean }> =>
  send(`/api/visits/${encodeURIComponent(visitId)}/report`, 'PUT', body);

/** One way. After this the report is read and never edited. */
export const submitReport = (visitId: string): Promise<{ ok: true; submittedAt: string }> =>
  send(`/api/visits/${encodeURIComponent(visitId)}/report/submit`, 'POST', {});

export const listVisitIssues = (visitId: string): Promise<{ issues: IssueRow[] }> =>
  request(`/api/visits/${encodeURIComponent(visitId)}/issues`);

export const raiseIssue = (visitId: string, body: NewIssue): Promise<{ id: string }> =>
  send(`/api/visits/${encodeURIComponent(visitId)}/issues`, 'POST', body);

export const listIssues = (state: IssueState | ''): Promise<{ issues: OfficeIssueRow[] }> =>
  request(`/api/issues${query({ state })}`);

export const patchIssue = (id: string, body: IssuePatch): Promise<{ ok: true }> =>
  send(`/api/issues/${encodeURIComponent(id)}`, 'PATCH', body);

/**
 * The office writes what the client reads of an issue, in its own words (ops #98). Only then does the
 * line reach the household's journal at home; an open issue is acknowledged by it.
 */
export const writeIssueForClient = (
  id: string,
  input: IssueForClientInput,
): Promise<{ ok: true; forClientAt: string }> =>
  send(`/api/issues/${encodeURIComponent(id)}/for-client`, 'PUT', input);

/* ------------------------------------------------------------------ *
 * Money. /api/invoices, /api/payments. Every read here asks for a
 * recent sign-in, and answers 403 step_up_required with a link.
 * ------------------------------------------------------------------ */

export interface InvoiceRow {
  readonly id: string;
  readonly clientId: string;
  readonly visitId: string;
  readonly state: InvoiceState;
  readonly issuedOn: string | null;
  readonly dueOn: string | null;
  readonly sentAt: string | null;
  readonly paidAt: string | null;
  readonly totalCents: number;
  readonly squareInvoiceId: string | null;
  readonly createdAt: string;
  readonly visitDate: string;
  readonly visitKind: VisitKind;
  readonly clientName: string;
  /**
   * `FK-42`, given at its first send (ops #76 O2): null on a draft, and on an invoice sent before
   * numbering began, whose Zelle memo is then the day of its clean.
   */
  readonly number: string | null;
}

export interface InvoiceListRow extends InvoiceRow {
  readonly balanceCents: number;
  /** Past its due date with money still owed, as the server's day reckons it. */
  readonly late: boolean;
}

export interface InvoiceLineRow {
  readonly id: string;
  readonly invoiceId: string;
  readonly position: number;
  readonly rateLineCode: string | null;
  readonly description: string;
  readonly quantity: number;
  readonly unitPriceCents: number;
  readonly amountCents: number;
  readonly priceSource: PriceSource;
  readonly agreedAt: string;
}

/** A payment's share of this invoice, with enough of the payment to say how and when. */
export interface AllocationRow {
  readonly id: string;
  readonly paymentId: string;
  readonly amountCents: number;
  readonly method: PaymentMethod;
  readonly entrySource: EntrySource;
  readonly receivedOn: string;
  readonly reference: string | null;
  /** The payment's receipt, `FK-R7`, or null for a refund, a correction or one before numbering. */
  readonly receiptNumber: string | null;
  /**
   * Whether ops's receipt email for it is on its way or gone (ops #76 O6). OPTIONAL BECAUSE A GRADUAL
   * DEPLOYMENT SPLITS CODE: absent says nothing of a receipt email.
   */
  readonly receiptEmailed?: boolean;
}

/** A link that was made. The token itself is not here: it existed once, in the link. */
export interface GrantRow {
  readonly id: string;
  readonly contactId: string;
  readonly expiresAt: string;
  readonly revokedAt: string | null;
  readonly firstOpenedAt: string | null;
  readonly openCount: number;
  readonly createdAt: string;
}

/**
 * "HOW DID WE DO?" ON ONE INVOICE, as the office sees it (ops #46, step 2): where the question the
 * invoice message asked stands, and whether it is held. Null on the invoice when nobody was asked.
 */
export interface InvoiceSurvey {
  readonly state: SurveyState;
  /** When the invoice message that asked it was written. */
  readonly askedAt: string;
  /** Who it went to. */
  readonly contactName: string | null;
  readonly deliveredAt: string | null;
  readonly reminderDueAt: string | null;
  readonly reminded: boolean;
  readonly callDueAt: string | null;
  readonly callRaised: boolean;
  readonly latestFace: FeedbackFace | null;
  readonly answeredAt: string | null;
  /** Held until a person releases it, which stops everything further on the invoice. */
  readonly held: boolean;
  readonly holdReason: SurveyHoldReason | null;
  /** The issue an answer below happy opened. */
  readonly issueId: string | null;
}

/** One answer she gave on the invoice, from her link or through the office. */
export interface InvoiceAnswer {
  readonly id: string;
  readonly face: FeedbackFace;
  readonly area: string | null;
  readonly line: string | null;
  readonly source: FeedbackSource;
  /** The person who typed it at the office, and null for any other door. */
  readonly recordedByName: string | null;
  readonly createdAt: string;
}

export interface InvoiceDetail {
  readonly invoice: InvoiceRow;
  readonly lines: readonly InvoiceLineRow[];
  readonly allocations: readonly AllocationRow[];
  readonly grants: readonly GrantRow[];
  readonly balanceCents: number;
  readonly survey: InvoiceSurvey | null;
  /** Newest first. */
  readonly answers: readonly InvoiceAnswer[];
  /** The household's saved method, if one stands, and whether it pays this invoice (step 3b). */
  readonly autopay: InvoiceAutopay | null;
  /** The automatic charge the invoice carries, or null for one issued with no saved method (4b). */
  readonly charge: InvoiceCharge | null;
  /**
   * A bank payment through Stripe on its way, when it began and how much it is, while it still stands
   * in the way of another page; null while none does (the staging walk of 2026-09-23, and its review).
   * While it clears the screen sends no invoice, and money typed against it asks the office first.
   */
  readonly bankPaymentClearing: { readonly since: string; readonly amountCents: number } | null;
  /** Her report through home that she sent its money by Zelle, or null where she made none (ops #71). */
  readonly zelleReport: InvoiceZelleReport | null;
  /**
   * Every email about the invoice and about money that landed on it, newest first, each with where it
   * stands (ops #76 O6). OPTIONAL BECAUSE A GRADUAL DEPLOYMENT SPLITS CODE: absent draws no Emails card.
   */
  readonly emails?: readonly OfficeEmailRow[];
  /**
   * Her invoice page at home and every live door of the household with its role, on a host whose
   * clients reach home (`CLIENT_PAGES_AT_HOME`, `docs/plan-clients-only-home.md` concern B1d), else null.
   * OPTIONAL BECAUSE A GRADUAL DEPLOYMENT SPLITS CODE: absent reads as null, the screens of today.
   */
  readonly atHome?: InvoiceAtHome | null;
}

/**
 * WHERE HER INVOICE IS AT HOME, as the office's two screens read it (concern B1d, D8): the page's whole
 * address, composed on the server from home's origin, and the household's live doors there, `invited`
 * or `granted`, each with its role. A person with a `client` door is shown the page; anyone else is told
 * why not, and no link of ours is made.
 */
export interface InvoiceAtHome {
  readonly invoiceUrl: string;
  readonly doors: readonly { readonly contactId: string; readonly role: MembershipRole }[];
}

/**
 * ONE CLIENT EMAIL, AS THE OFFICE READS WHERE IT STANDS (ops #76 O6, `docs/plan-o6-o9-emails.md` section
 * 3.7): what ops composed and why it parked one, and what home reported of it, from the three rows the
 * one writer wrote together. `emailStanding` (`screens/visit-detail/shared.ts`) says it in words. Her
 * address is never here whole, and the words with her link never at all.
 */
export interface OfficeEmailRow {
  /** The intent: the email as ops composed it. */
  readonly id: string;
  readonly kind: EmailKind;
  readonly contactId: string;
  readonly contactName: string;
  /** Where it went, shown and not given: her address's first letter and its domain. */
  readonly toMasked: string | null;
  /** Its subject, on the invoice screen alone; null on the household's card. */
  readonly subject: string | null;
  readonly state: MessageIntentState;
  readonly failureCode: FailureCode | null;
  readonly createdAt: string;
  readonly refusedAt: string | null;
  /** When home took it from ops. */
  readonly handedOffAt: string | null;
  readonly cancelledAt: string | null;
  /** When home reported Microsoft accepted it. */
  readonly acceptedAt: string | null;
  readonly deliveredAt: string | null;
  readonly failedAt: string | null;
  readonly bouncedLateAt: string | null;
  /** Home's own word on a failure. */
  readonly homeSaid: string | null;
  /** Why ops parked it, while parked: the register's, the switch's or a withdrawal's words. */
  readonly parkedWith: string | null;
  /** It waits in ops for its invoice's PDF to be made. */
  readonly waitingForPdf: boolean;
  /**
   * It waits in ops for her door at home, the push of which home has not taken yet. OPTIONAL BECAUSE A
   * GRADUAL DEPLOYMENT SPLITS CODE: absent says nothing of it.
   */
  readonly waitingForDoor?: boolean;
  readonly invoiceId: string | null;
  readonly paymentId: string | null;
  readonly visitId: string | null;
  /**
   * On the invoice screen, an email about another invoice whose words name this one beside its own: a
   * past-due note's "also past due" sentence. OPTIONAL BECAUSE A GRADUAL DEPLOYMENT SPLITS CODE: absent
   * says nothing of it.
   */
  readonly namesIt?: boolean;
  /** The number of the invoice the email is about, on the invoice screen alone; null on the household's. */
  readonly invoiceNumber?: string | null;
}

/** A page of a household's emails: the newest `limit`, newest first, and `more` when older are kept. */
export interface HouseholdEmailsPage {
  readonly limit: number;
  readonly more: boolean;
  readonly emails: readonly OfficeEmailRow[];
}

/** Every email ops composed to one household's people (ops #76 O6). Office only. */
export const getHouseholdEmails = (clientId: string): Promise<HouseholdEmailsPage> =>
  request(`/api/messages/emails/client/${encodeURIComponent(clientId)}`);

/**
 * HER REPORT THROUGH HOME THAT SHE SENT THE INVOICE'S MONEY BY ZELLE (ops #71 item 5), as the office
 * reads it: the day she gave, when ops received the report that stands, who said it, the office's clear
 * and who made it, and whether it stands now, which the server decides by the rule the push to home
 * keeps: reported, not cleared, and money still owed. It never marks anything paid.
 */
export interface InvoiceZelleReport {
  readonly sentOn: string;
  readonly reportedAt: string;
  readonly by: string | null;
  readonly clearedAt: string | null;
  readonly clearedBy: string | null;
  readonly stands: boolean;
}

/**
 * The household's saved method on one invoice, and whether this invoice is one it pays: the charge
 * the invoice carries still stands and Stripe is on here (ops #46, step 4b). When it does, her page
 * asks how the clean went and offers Zelle in the charge's place, and the words "Send the invoice"
 * offers say her method is charged. A charge that ended any way but paid hands it back to every way.
 * For an invoice it pays, and only then, when it is charged and whether its receipt goes by email,
 * which those words say by the rule her page keeps (the review of the staging walk of 2026-09-23).
 */
export interface InvoiceAutopay extends AutopayView {
  readonly coversThisInvoice: boolean;
  readonly whenCharged: WhenCharged | null;
}

/**
 * When a saved method is charged, and whether its receipt goes by email: the business day an owner
 * set, `HH:MM` in Florida with the days as JavaScript numbers them, 0 for Sunday, and whether the
 * household's payer has an email the register lets ops write to.
 */
export interface WhenCharged {
  readonly businessHours: {
    readonly start: string;
    readonly end: string;
    readonly days: readonly number[];
  };
  readonly receiptByEmail: boolean;
}

/**
 * THE AUTOMATIC CHARGE ON ONE INVOICE, as the office sees it (ops #46, step 4): where it stands and
 * why, what Stripe was asked for and when, the office's release, and whether it is the first under
 * her saved method, which waits for her own good answer and which the office cannot release.
 */
export interface InvoiceCharge {
  readonly state: InvoiceChargeState;
  readonly gateReason: ChargeGateReason | null;
  readonly holdReason: ChargeHoldReason | null;
  readonly cancelReason: ChargeCancelReason | null;
  readonly attempts: number;
  /** The balance Stripe was asked for, and null before any try. */
  readonly amountCents: number | null;
  /** A decline in words the office can say to her, never Stripe's own code. */
  readonly declineWords: string | null;
  readonly chargedAt: string | null;
  readonly settledAt: string | null;
  readonly releasedAt: string | null;
  readonly releasedByName: string | null;
  readonly firstCharge: boolean;
  /** "Visa 4242": the method it is charged to, in the words the office reads it by. */
  readonly methodLabel: string;
  readonly methodType: SavedMethodType | null;
  /** When she pressed "I'm paying by Zelle", and who her link was for (2026-09-23). */
  readonly pausedAt: string | null;
  readonly pausedByName: string | null;
  /** The office's lift of her pause, and who lifted it. */
  readonly pauseLiftedAt: string | null;
  readonly pauseLiftedByName: string | null;
  /** When the office was told her Zelle had not come in its business days. */
  readonly pauseChasedAt: string | null;
  readonly createdAt: string;
  /**
   * What the job's next run decides for a charge no try has claimed, as the invoice stands at the read
   * (the staging walk of 2026-09-23): the row moves only when a run writes it, so her answer or her
   * pause shows here the moment it is recorded. Null once a try has claimed it or it has ended, and
   * where Stripe is off, since then no run decides anything.
   */
  readonly nextRun: ChargeNextRun | null;
}

/**
 * The next run's decision on a charge: charge, hold or cancel it and why, wait, and for what, or give
 * up on one the gate opens when ops holds nothing to charge the saved method through, which the run
 * ends failed and puts in front of the office.
 */
export type ChargeNextRun =
  | { readonly decision: 'charge'; readonly reason: ChargeGateReason }
  | { readonly decision: 'hold'; readonly reason: ChargeHoldReason }
  | { readonly decision: 'cancel'; readonly reason: ChargeCancelReason }
  | { readonly decision: 'wait'; readonly why: 'her_answer' | 'paying_elsewhere' }
  | { readonly decision: 'give_up'; readonly why: 'unchargeable' };

export interface PaymentRow {
  readonly id: string;
  readonly clientId: string;
  readonly kind: PaymentKind;
  readonly method: PaymentMethod;
  readonly entrySource: EntrySource;
  /** Signed: a payment is positive, a refund negative. Excludes any tip. */
  readonly amountCents: number;
  readonly tipCents: number;
  readonly receivedOn: string;
  readonly squarePaymentId: string | null;
  readonly reference: string | null;
  readonly createdAt: string;
  /** `FK-R7`, given as a payment is recorded (ops #76 O2); null on a refund and a correction. */
  readonly receiptNumber: string | null;
}

export interface PaymentWithAllocations extends PaymentRow {
  readonly allocations: readonly {
    readonly id: string;
    readonly paymentId: string;
    readonly invoiceId: string;
    readonly amountCents: number;
  }[];
}

export interface NewPayment {
  readonly clientId: string;
  readonly kind: PaymentKind;
  readonly method: PaymentMethod;
  readonly entrySource: EntrySource;
  readonly amountCents: number;
  readonly tipCents?: number;
  readonly receivedOn: string;
  readonly squarePaymentId?: string | null;
  readonly reference?: string | null;
  readonly allocations: readonly { readonly invoiceId: string; readonly amountCents: number }[];
  /**
   * The office's word that this is separate money from a bank payment clearing on the invoice, which
   * the server requires while one clears and refuses `409` `bank_payment_clearing` without (the review
   * of the staging walk of 2026-09-23). Sent only when the office ticked it.
   */
  readonly despiteClearing?: boolean;
}

export const listInvoices = (
  open: boolean,
  clientId: string | null,
  returnPath: string,
): Promise<{ today: string; invoices: InvoiceListRow[] }> =>
  request(`/api/invoices${query({ open: open ? '1' : '0', clientId, return: returnPath })}`);

export const getInvoice = (id: string, returnPath: string): Promise<InvoiceDetail> =>
  request(`/api/invoices/${encodeURIComponent(id)}${query({ return: returnPath })}`);

/** From a finished visit and nothing else. The lines come from the visit's own agreed figures. */
export const draftInvoice = (
  visitId: string,
): Promise<{ id: string; totalCents: number; lines: number }> =>
  send('/api/invoices', 'POST', { visitId });

/**
 * WHAT BECAME OF THE INVOICE EMAIL THE SEND COMPOSED (ops #76 O6), as `api/src/money/pay-link.ts`
 * answers it: composed for the one who pays and handed to home once its PDF is made; written and
 * parked, the register or the switch for client email refusing it, or its words held; or nothing
 * composed, with no address on file for the one who pays or nobody on the household.
 */
export type InvoiceEmailOutcome =
  | 'waiting_for_pdf'
  | 'refused_by_consent'
  | 'not_enabled'
  | 'not_chosen'
  | 'held'
  | 'no_address'
  | 'no_contact';

export interface InvoiceEmailAnswer {
  readonly outcome: InvoiceEmailOutcome;
  /** The person it is to, by name, or null where the household has nobody. */
  readonly to: string | null;
  readonly intentId: string | null;
  /**
   * What the register said, on a refusal by it alone, in its own words: no yes to email on record, a
   * stop, or a do-not-contact mark with why and since when. Absent on every other outcome, and from a
   * Worker older than the field, where the screen claims no reason rather than a wrong one.
   */
  readonly refusal?: string;
  /** Her choice's or the office's hold's words on `not_chosen` alone (ops #76 phase 2). */
  readonly choice?: string;
  /** Set on `not_chosen` where the office is given the call. */
  readonly officeCalls?: boolean;
}

export const sendInvoice = (
  id: string,
  body: { squareInvoiceId: string | null; issuedOn: string | null },
): Promise<{
  ok: true;
  issuedOn: string;
  dueOn: string;
  totalCents: number;
  /** The number the send gave it, `FK-42` (ops #76 O2). */
  number: string | null;
  /** What became of the invoice email composed with it (ops #76 O6). */
  email: InvoiceEmailAnswer;
}> => send(`/api/invoices/${encodeURIComponent(id)}/send`, 'POST', body);

export const voidInvoice = (id: string): Promise<{ ok: true }> =>
  send(`/api/invoices/${encodeURIComponent(id)}/void`, 'POST', {});

/**
 * ONE OF AN INVOICE'S PDFs AS THE OFFICE SEES IT (ops #76 O3): the invoice, its cancellation, or the
 * receipt of a payment applied to it, where it stands, and what stopped it or what its render changed.
 * `waitingForAddress` is a pending one held until the address on invoices and receipts is set.
 */
export interface InvoiceDocument {
  readonly documentId: string;
  readonly kind: DocumentKind;
  readonly state: DocumentState;
  readonly number: string;
  readonly issuedOn: string;
  readonly fileName: string;
  readonly paymentId: string | null;
  readonly attempts: number;
  readonly failure: string | null;
  readonly renderNote: string | null;
  readonly renderedAt: string | null;
  readonly failedAt: string | null;
  readonly waitingForAddress: boolean;
}

/** The invoice's PDFs, in the order they were pressed. A money read, behind a recent sign-in. */
export const listInvoiceDocuments = (
  id: string,
  returnPath: string,
): Promise<{ documents: InvoiceDocument[] }> =>
  request(`/api/invoices/${encodeURIComponent(id)}/documents${query({ return: returnPath })}`);

/**
 * A link to one of them for the person signed in, minted at the press and good for ten minutes: 409
 * `document_not_ready` while it is not made, 404 for one not this invoice's, 503 where nothing can sign
 * a link. It writes nothing.
 */
export const invoiceDocumentLink = (
  invoiceId: string,
  documentId: string,
): Promise<DocumentLinkAnswer> =>
  send(
    `/api/invoices/${encodeURIComponent(invoiceId)}/documents/${encodeURIComponent(documentId)}/link`,
    'POST',
    {},
  );

/** "Try again" on one given up: back to being made, which happens after the answer. 409 `not_failed`. */
export const retryInvoiceDocument = (
  invoiceId: string,
  documentId: string,
): Promise<{ ok: true }> =>
  send(
    `/api/invoices/${encodeURIComponent(invoiceId)}/documents/${encodeURIComponent(documentId)}/retry`,
    'POST',
    {},
  );

/** The page Stripe makes to pay an invoice through: here with its link, or still being made. */
export type PaymentPageAnswer =
  | { readonly state: 'ready'; readonly url: string; readonly expiresAt: string }
  | { readonly state: 'creating' };

/**
 * "Send the invoice": her link to it, in words she reads, sent to one person of the household through
 * the phone system and never from here. The words must carry the link made for her, whole.
 */
export const sendPayLink = (
  id: string,
  body: { contactId: string; channel: 'sms' | 'email'; body: string },
): Promise<{
  intentId: string;
  refusedByConsent: boolean;
  gateRefusal: string | null;
  /**
   * What the register said, on a refusal by it, in its own words: no yes on record, a stop, or a
   * do-not-contact mark with why and since when. Absent otherwise, and from a Worker older than the
   * field, where the screen claims no reason rather than a wrong one (concern 14's review).
   */
  refusal?: string;
  /** Her choice's or the office's hold's words when they refused it (ops #76 phase 2), or null. */
  choiceRefusal?: string | null;
  /** Set where the office is given the call instead. */
  officeCalls?: boolean;
}> => send(`/api/invoices/${encodeURIComponent(id)}/pay-link/send`, 'POST', body);

/** One face, and the part of the home and one line of her words if she gave them. */
export interface FeedbackBody {
  readonly face: FeedbackFace;
  readonly area?: string | null;
  readonly line?: string | null;
}

/** What an answer came to: whether it was written, and what she said last on the invoice. */
export interface FeedbackRecorded {
  readonly recorded: boolean;
  readonly latestFace: FeedbackFace | null;
}

/** Nothing further happens on the invoice until a person releases it. */
export const holdSurvey = (invoiceId: string): Promise<{ held: true }> =>
  send(`/api/invoices/${encodeURIComponent(invoiceId)}/survey/hold`, 'POST', {});

export const releaseSurvey = (invoiceId: string): Promise<{ held: false }> =>
  send(`/api/invoices/${encodeURIComponent(invoiceId)}/survey/release`, 'POST', {});

/** The answer she gave on the phone, recorded by the office. */
export const answerSurvey = (invoiceId: string, body: FeedbackBody): Promise<FeedbackRecorded> =>
  send(`/api/invoices/${encodeURIComponent(invoiceId)}/survey/answer`, 'POST', body);

/**
 * "Charge her saved card now": a charge that is not the first under her saved method, made on the
 * job's next run inside business hours without her tap. The server refuses the first, which waits
 * for her own answer, and any charge already at Stripe or ended.
 */
export const releaseCharge = (invoiceId: string): Promise<{ released: true }> =>
  send(`/api/invoices/${encodeURIComponent(invoiceId)}/charge/release`, 'POST', {});

/** "Do not charge this one": the invoice goes back to every way to pay. */
export const cancelCharge = (invoiceId: string): Promise<{ cancelled: true }> =>
  send(`/api/invoices/${encodeURIComponent(invoiceId)}/charge/cancel`, 'POST', {});

/**
 * "Lift the pause": she said she is paying this one by Zelle, and the office lets the charge go ahead
 * as it would have. Refused `not_paused` where she never pressed, and once a try is at Stripe.
 */
export const liftChargePause = (invoiceId: string): Promise<{ lifted: true }> =>
  send(`/api/invoices/${encodeURIComponent(invoiceId)}/charge/lift-pause`, 'POST', {});

/**
 * What her saved method's charge does once her Zelle report is cleared, as the server says it: none
 * pays the invoice, her pause stands beside it until the office lifts it, or it goes ahead as it would
 * have, held by her report alone (the final review of 2026-10-17).
 */
export type ChargeAfterClear = 'none' | 'stays_paused' | 'goes_ahead';

/**
 * "Clear the Zelle report": she said through home that she sent it by Zelle, and the office says it
 * is not coming, so her page stops holding the invoice as on its way. It touches no charge, and says
 * what the charge does next. Refused `nothing_reported` where no report of hers stands.
 */
export const clearZelleReport = (
  invoiceId: string,
): Promise<{ cleared: true; chargeNext: ChargeAfterClear }> =>
  send(`/api/invoices/${encodeURIComponent(invoiceId)}/zelle-report/clear`, 'POST', {});

export const listPayments = (
  clientId: string,
  returnPath: string,
): Promise<{ payments: PaymentWithAllocations[] }> =>
  request(`/api/payments${query({ clientId, return: returnPath })}`);

export const recordPayment = (
  body: NewPayment,
): Promise<{
  id: string;
  /** `FK-R7` for a payment, null for a refund or a correction (ops #76 O2). */
  receiptNumber: string | null;
  allocatedCents: number;
  unallocatedCents: number;
}> => send('/api/payments', 'POST', body);

/* ------------------------------------------------------------------ *
 * The household's timeline, its glance, and the page of a home (ops #84).
 * /api/clients/:id/timeline, /api/clients/:id/glance, /api/homes/:id.
 * ------------------------------------------------------------------ */

/** A change as the breach list returns it: the raw row, with no intents. */
export interface ChangeRecord {
  readonly id: string;
  readonly visitId: string;
  readonly kind: VisitChangeKind;
  readonly fromDate: string | null;
  readonly toDate: string | null;
  readonly note: string | null;
  readonly toldAt: string | null;
  readonly silenceReason: SilenceReason | null;
  readonly inside24h: boolean;
  readonly chargeDecision: ChargeDecision;
  readonly changedBy: string | null;
  readonly createdAt: string;
}

/**
 * ONE ROW OF A HOUSEHOLD'S TIMELINE (ops #84, `docs/plan-client-workplace.md` section 3.1), its words
 * written by the server: the screen draws the title, the detail, who and the pill, and decides none of
 * them. `clientView` is the server's call on whether the client saw it or did it.
 */
export interface TimelineRow {
  readonly kind: TimelineKind;
  readonly id: string;
  /** The instant it is ordered by. */
  readonly at: string;
  /** The day alone when the record holds no time (a payment, a visit with no window), else null. */
  readonly on: string | null;
  readonly homeId: string | null;
  readonly clientView: boolean;
  readonly who: { readonly name: string } | null;
  readonly title: string;
  readonly detail: string | null;
  /** The ops path the row opens; a call's page only for an owner or a manager. */
  readonly link: string | null;
  readonly state: string | null;
  readonly tone: 'quiet' | 'gold' | 'sage' | 'coral';
}

export interface TimelinePage {
  readonly client: {
    readonly id: string;
    readonly displayName: string;
    readonly status: ClientStatus;
  };
  readonly today: string;
  /** The window the page was read over, the server's defaults filled in. */
  readonly from: string;
  readonly to: string;
  /** The household's homes, for the Home filter. */
  readonly homes: readonly { readonly id: string; readonly label: string }[];
  readonly view: TimelineView;
  readonly rows: readonly TimelineRow[];
  /** The cursor of the page beneath, `<at>/<id>`, or null at the end. */
  readonly next: string | null;
}

/** What the timeline is narrowed by. Empty kinds is every kind; an empty string is no filter. */
export interface TimelineFilters {
  readonly kinds: readonly TimelineKind[];
  readonly home: string;
  /**
   * THE HOUSEHOLD CHIP ON A HOME'S PAGE (ops #84 phase 2): with a home asked for, the household's own
   * rows, those tied to no home, beside the home's. Meaningless with no home, so never sent then.
   */
  readonly household: boolean;
  readonly from: string;
  readonly to: string;
  readonly view: TimelineView;
}

/**
 * One page of the household's timeline, beneath `before` when it names one. `returnPath` is where a
 * sign-in brings the person back to, filters and all, since the money on it asks for a recent one.
 */
export const getClientTimeline = (
  clientId: string,
  filters: TimelineFilters,
  before: string | null,
  returnPath: string,
): Promise<TimelinePage> =>
  request(
    `/api/clients/${encodeURIComponent(clientId)}/timeline${query({
      kinds: filters.kinds.join(','),
      home: filters.home,
      household: filters.home !== '' && filters.household ? '1' : null,
      from: filters.from,
      to: filters.to,
      view: filters.view === 'client' ? 'client' : null,
      before,
      return: returnPath,
    })}`,
  );

/**
 * AN AGREEMENT AS A LINE (ops #84 phase 2): what it is, the price the card gave and the revision that
 * gave it, and the pause its standing schedule holds, from a day and until one or open-ended.
 */
export interface AgreementLine {
  readonly id: string;
  readonly serviceLine: ServiceLine;
  readonly cadence: Cadence;
  readonly priceCents: number;
  readonly priceSource: PriceSource;
  readonly state: AgreementState;
  readonly startedOn: string;
  readonly endedOn: string | null;
  readonly cardRevision: number | null;
  readonly pausedFrom: string | null;
  readonly pausedUntil: string | null;
}

/** The household as the glance and a home's page name it, for the way back to its page. */
export interface HouseholdNamed {
  readonly id: string;
  readonly displayName: string;
  readonly status: ClientStatus;
  readonly endedOn: string | null;
}

/**
 * THE HOUSEHOLD AT A GLANCE (ops #84, `docs/plan-client-workplace.md` section 4): the facts the band at
 * the top of the client's page fills its fixed sentences with. Nothing in it is a sentence.
 */
export interface ClientGlance {
  readonly client: HouseholdNamed;
  readonly today: string;
  /** The first clean still to happen or under way; its home's words where there are two or more. */
  readonly nextVisit: {
    readonly id: string;
    readonly status: VisitStatus;
    readonly scheduledDate: string;
    readonly windowStart: string | null;
    readonly windowEnd: string | null;
    readonly homeId: string;
    readonly homeLabel: string | null;
    /** First names, in the order of the alphabet. */
    readonly cleaners: readonly string[];
  } | null;
  /** What is owed on the invoices sent and not settled, or held back until a fresh sign-in. */
  readonly owed:
    | {
        readonly shown: true;
        readonly cents: number;
        readonly invoices: number;
        readonly oldestDueOn: string | null;
        readonly overdue: boolean;
      }
    | { readonly shown: false; readonly signInPath: string };
  readonly asks: { readonly waiting: number; readonly oldestAt: string | null };
  /** The timeline's newest row of what passed between them, as the client saw it, or null. */
  readonly lastContact: {
    readonly kind: TimelineKind;
    readonly at: string;
    readonly on: string | null;
    readonly title: string;
    readonly who: { readonly name: string } | null;
  } | null;
  readonly reach: readonly {
    readonly contactId: string;
    readonly name: string;
    readonly role: ContactRole;
    readonly texts: boolean;
    readonly textsBasis: 'website_box' | null;
    readonly email: boolean;
    /** Open, marked do not call, or no number to call at all, whatever the marks say. */
    readonly calls: 'open' | 'marked' | 'no_number';
    readonly marks: Readonly<Record<MarkChannel, DoNotContactReason | null>>;
    /** A stop in their own words waits on the office, on their number. */
    readonly held: boolean;
  }[];
  readonly agreements: readonly (AgreementLine & {
    readonly homeId: string;
    readonly homeLabel: string | null;
  })[];
  readonly onHome: readonly {
    readonly contactId: string;
    readonly name: string;
    readonly state: Extract<MembershipState, 'invited' | 'granted'>;
  }[];
}

/**
 * The glance of one household. Never refused for a stale sign-in: the money alone is held back, with
 * the server's way to sign in again, which brings the person back to `returnPath`.
 */
export const getClientGlance = (clientId: string, returnPath: string): Promise<ClientGlance> =>
  request(`/api/clients/${encodeURIComponent(clientId)}/glance${query({ return: returnPath })}`);

/** A clean still to happen or under way on one home, with the first names of who cleans it. */
export interface OpenClean {
  readonly id: string;
  readonly kind: VisitKind;
  readonly status: VisitStatus;
  readonly scheduledDate: string;
  readonly windowStart: string | null;
  readonly windowEnd: string | null;
  readonly reclean: boolean;
  readonly cleaners: readonly string[];
}

/**
 * THE PAGE OF ONE HOME (ops #84, section 5): the household for the way back, the home, its agreements,
 * its next cleans, and the household's people with the marks that hold on each. Never a code.
 */
export interface HomeRecord {
  readonly client: HouseholdNamed;
  readonly today: string;
  readonly home: SiteRow;
  readonly agreements: readonly AgreementLine[];
  readonly visits: readonly OpenClean[];
  readonly people: readonly {
    readonly id: string;
    readonly displayName: string;
    readonly role: ContactRole;
    readonly phoneE164: string | null;
    readonly email: string | null;
    readonly doNotContact: MarksHeld;
  }[];
}

export const getHome = (id: string): Promise<HomeRecord> =>
  request(`/api/homes/${encodeURIComponent(id)}`);

/**
 * WHAT THEY CHECK FIRST, SET OR CLEARED ON THE HOME'S PAGE (ops #86): the three boxes joined by
 * newlines, or null to clear. The server trims each line, refuses a fourth, a text past 400 characters,
 * a code or a claim, and answers what it kept.
 */
export const setChecksFirst = (
  siteId: string,
  checksFirst: string | null,
): Promise<{ ok: true; checksFirst: string | null }> =>
  send(`/api/homes/${encodeURIComponent(siteId)}`, 'PATCH', { checksFirst });

/** One version of a home plan as the office reads it (ops #74 item 6). */
export interface HomePlanVersion {
  readonly id: string;
  readonly version: number;
  readonly body: string;
  readonly contentHash: string;
  readonly source: HomePlanSource;
  readonly createdAt: string;
  readonly approvedAt: string | null;
  readonly approvedByName: string | null;
  /** The Florida day it took effect; null on a draft. */
  readonly effectiveFrom: string | null;
}

/**
 * THE HOME IN THE HOUSEHOLD'S OWN WORDS (ops #74 item 6): the version a sheet opened now freezes, and
 * the draft the office typed and nobody has approved yet, which no sheet shows.
 */
export interface HomePlan {
  readonly live: HomePlanVersion | null;
  readonly draft: HomePlanVersion | null;
}

export const getHomePlan = (siteId: string): Promise<HomePlan> =>
  request(`/api/homes/${encodeURIComponent(siteId)}/plan`);

/**
 * A new version, unapproved, in place of a draft that stands. The server refuses a body that looks
 * like a code, an empty one and one past 4000 characters, and answers the plan as it now stands.
 */
export const writeHomePlan = (
  siteId: string,
  body: { body: string; source: Exclude<HomePlanSource, 'client_via_home'> },
): Promise<HomePlan> => send(`/api/homes/${encodeURIComponent(siteId)}/plan`, 'PUT', body);

/** The draft approved, named by the id read, which ends the version before it. */
export const approveHomePlan = (siteId: string, id: string): Promise<HomePlan> =>
  send(`/api/homes/${encodeURIComponent(siteId)}/plan/approve`, 'POST', { id });

/** Where a key is (ops #100): at the office, with a cleaner, or no longer the company's to hold. */
export type KeyPlace = 'office' | 'cleaner' | 'gone';

/** A person named on a movement of a key, by id and the name the office knows them by. */
export interface KeyPerson {
  readonly id: string;
  readonly displayName: string;
}

/** One movement of one key of a home, as the office reads the chain (ops #100). */
export interface KeyMovement {
  readonly id: string;
  readonly keyKind: KeyKind;
  readonly keyLabel: string;
  readonly event: KeyCustodyEvent;
  /** ISO UTC instant. Rendered in America/New_York. */
  readonly occurredAt: string;
  /** The cleaner it went to, came back from, or was lost or destroyed by; null otherwise. */
  readonly cleaner: KeyPerson | null;
  /** The household's person who gave it or took it back; null where none was named. */
  readonly contactId: string | null;
  readonly note: string | null;
  /** Who typed it. */
  readonly recordedBy: KeyPerson;
}

/** One key of a home as it stands now (ops #100). */
export interface KeyNow {
  readonly keyKind: KeyKind;
  readonly keyLabel: string;
  readonly at: KeyPlace;
  /** The cleaner who holds it, while one does. */
  readonly cleaner: KeyPerson | null;
  /** When its latest movement happened, an ISO UTC instant. */
  readonly since: string;
}

/**
 * THE KEYS OF A HOME (ops #100): every movement newest first, and every key as it stands, the keys
 * the company holds before the gone ones. Office only on the server.
 */
export interface HomeKeys {
  readonly chain: readonly KeyMovement[];
  readonly keys: readonly KeyNow[];
}

/**
 * ONE MOVEMENT OF ONE KEY, as the office records it. `cleanerId` names the cleaner of a hand or a
 * return and is sent for nothing else; `occurredAt` is an instant with its offset. The server holds
 * every rule: the key's state, the checks mark, a code in the name or the note, a time to come.
 */
export interface NewKeyMovement {
  readonly keyKind: KeyKind;
  readonly keyLabel: string;
  readonly event: KeyCustodyEvent;
  readonly cleanerId?: string;
  readonly contactId?: string;
  readonly occurredAt: string;
  readonly note?: string;
}

export const getHomeKeys = (siteId: string): Promise<HomeKeys> =>
  request(`/api/homes/${encodeURIComponent(siteId)}/keys`);

/** Answers `201` with the movement written and where the key is now. */
export const recordKeyMovement = (
  siteId: string,
  body: NewKeyMovement,
): Promise<{ movement: KeyMovement; key: KeyNow }> =>
  send(`/api/homes/${encodeURIComponent(siteId)}/keys`, 'POST', body);

/** One key a cleaner holds, with the home it opens (ops #100): the offboarding list. */
export interface CleanerKey {
  readonly keyKind: KeyKind;
  readonly keyLabel: string;
  readonly since: string;
  readonly home: {
    readonly id: string;
    readonly label: string | null;
    readonly addressLine1: string;
  };
}

/** Every key the cleaner holds now, across homes. Office only on the server. */
export const getCleanerKeys = (cleanerId: string): Promise<{ keys: CleanerKey[] }> =>
  request(`/api/cleaners/${encodeURIComponent(cleanerId)}/keys`);

/**
 * WHERE A NUMBER OR A WEBSITE LEAD LEADS (ops #84, section 6): the household, a short choice of
 * households, the enquiry, or nobody yet. Asked by the find page the two walls open; the answer
 * names the record and never says the number back.
 */
export type Found =
  | {
      readonly kind: 'client';
      readonly clientId: string;
      readonly contactId: string | null;
      readonly name: string;
    }
  | {
      readonly kind: 'choose';
      readonly households: readonly {
        readonly clientId: string;
        readonly contactId: string;
        readonly name: string;
        readonly person: string;
        readonly status: string;
      }[];
    }
  | { readonly kind: 'enquiry'; readonly enquiryId: string; readonly name: string }
  | { readonly kind: 'nobody' };

export const findRecord = (asked: { number: string } | { lead: string }): Promise<Found> =>
  request(`/api/find${query('number' in asked ? { number: asked.number } : { lead: asked.lead })}`);

/* ------------------------------------------------------------------ *
 * Throwing a row away. Two doors, one shape.
 * ------------------------------------------------------------------ */

/**
 * Why a row is being removed. The reason is required and is written into the row's own notes; the
 * server keeps no column for it, so this is the only place it is ever recorded.
 */
export interface Discard {
  readonly reason: DiscardReason;
  readonly notes: string | null;
}

/**
 * A test lead or a junk enquiry. Soft delete: the row leaves the list, the record and the reason
 * stay in the audit trail, and the website is told when it minted the lead. A second attempt on
 * the same enquiry answers 404. A privacy request answers `phoneSystem` too, whether the phone
 * system was asked to erase her texts (ops #27), which the sheet then says.
 */
export const discardEnquiry = (
  id: string,
  body: Discard,
): Promise<{ ok: true; phoneSystem?: PhoneForgetOutcome }> =>
  send(`/api/enquiries/${encodeURIComponent(id)}`, 'DELETE', body);

/** A test call on a household's page. Nobody is told: the call is the phone system's own fact. */
export const discardClientCall = (
  clientId: string,
  callId: string,
  body: Discard,
): Promise<{ ok: true }> =>
  send(
    `/api/clients/${encodeURIComponent(clientId)}/calls/${encodeURIComponent(callId)}`,
    'DELETE',
    body,
  );

/* ------------------------------------------------------------------ *
 * Client-facing links. /api/grants, and the one public read.
 * ------------------------------------------------------------------ */

export interface NewGrant {
  readonly targetKind: Extract<AccessGrantTarget, 'invoice' | 'visit_report'>;
  readonly targetId: string;
  readonly contactId: string;
}

/** The token is in this answer once. Show the path, let her copy it, and keep it nowhere. */
export interface GrantMade {
  readonly id: string;
  readonly token: string;
  readonly path: string;
  readonly expiresAt: string;
}

export const listGrants = (
  targetKind: NewGrant['targetKind'],
  targetId: string,
): Promise<{ grants: GrantRow[] }> => request(`/api/grants${query({ targetKind, targetId })}`);

export const createGrant = (body: NewGrant): Promise<GrantMade> =>
  send('/api/grants', 'POST', body);

export const revokeGrant = (id: string): Promise<{ ok: true }> =>
  request(`/api/grants/${encodeURIComponent(id)}`, { method: 'DELETE' });

export interface PublicInvoiceLine {
  readonly position: number;
  readonly description: string;
  readonly quantity: number;
  readonly unitPriceCents: number;
  readonly amountCents: number;
}

/**
 * WHAT HER INVOICE'S PAY PAGE NEEDS (ops #46), from the read: whether anything can be paid from it
 * now, whether a bank payment is already on its way, whether this deployment takes payments through
 * Stripe at all, which of Stripe's pages the invoice carries, and the Zelle block: the code's URL,
 * the tag and the name decoded from it on the server, and the memo. The page draws the code and
 * writes the two words from here and hardcodes neither.
 */
export interface PublicPay {
  readonly payable: boolean;
  readonly bankPaymentClearing: boolean;
  readonly online: boolean;
  readonly openPage: PayPageKind | null;
  /**
   * Whether the bank screen offers her own tick to save the account she pays from for the automatic
   * charge (2026-09-23): Stripe on, and nothing saved for the household yet.
   */
  readonly offerToSave: boolean;
  readonly zelle: {
    readonly qrUrl: string;
    readonly tag: string;
    readonly name: string;
    readonly memo: string | null;
  };
}

/** What she said last to "How did we do?", and when the question was asked: null for a link by hand. */
export interface PublicFeedback {
  readonly latestFace: FeedbackFace | null;
  readonly askedAt: string | null;
}

/**
 * THE SAVED METHOD THAT PAYS THIS INVOICE (ops #46, step 3b), on her page: its words and its kind, and
 * never an id. Null unless the household saved one before this invoice was issued and Stripe is on
 * here; then the page shows no bank or card screen. `stage` is where the charge stands for her (the
 * owner's decision of 2026-09-23): `waiting`, when she may still say she is paying this one by Zelle;
 * `paying_by_zelle`, she said so and it is not charged while the pause stands; `pause_lifted`, the
 * office lifted it; `at_stripe`, the charge is being made.
 */
export interface PublicAutopay {
  readonly displayLabel: string;
  readonly methodType: SavedMethodType | null;
  readonly stage: HerChargeStage;
  /**
   * The business day as an owner set it, which the automatic charge runs inside alone: `HH:MM` in
   * Florida, the start included and the end not, and the days as JavaScript numbers them, 0 for Sunday
   * (the staging walk of 2026-09-23).
   */
  readonly businessHours: {
    readonly start: string;
    readonly end: string;
    readonly days: readonly number[];
  };
  /**
   * Whether the charge's receipt goes by email: the household's payer has an email the register lets
   * ops write to. Where it does not, her page says nothing of a receipt.
   */
  readonly receiptByEmail: boolean;
}

/**
 * THE AUTOMATIC CHARGE THAT PAID THIS INVOICE (ops #54, item 1), on her page once its money arrived:
 * which of her saved methods, by its words and its kind and never an id, how much, when Stripe
 * answered, which for a bank account is when it was debited, and whether a receipt was emailed, which
 * is so only where Stripe emailed one. Null on an invoice still owed, or paid some other way.
 */
export interface PublicCharged {
  readonly displayLabel: string;
  readonly methodType: SavedMethodType | null;
  readonly amountCents: number;
  readonly chargedAt: string;
  readonly receiptEmailed: boolean;
}

/**
 * ONE OF HER INVOICE'S DOCUMENTS (ops #76 O8): the invoice's PDF, or the receipt of a payment applied
 * to it, by the id a press for it names. `ready` once it is drawn and kept; until then her page says
 * it is being made and offers no button for it.
 */
export interface PublicDocument {
  readonly documentId: string;
  readonly kind: 'invoice' | 'cancellation' | 'receipt';
  readonly number: string;
  readonly issuedOn: string;
  readonly fileName: string;
  readonly ready: boolean;
  readonly paymentId: string | null;
}

/** A link to one document, minted at her press: good for ten minutes, on ops's own origin. */
export interface DocumentLinkAnswer {
  readonly url: string;
  readonly expiresAt: string;
  readonly fileName: string;
  readonly sha256: string;
}

/** What a person holding a link is shown. Projected by the server for a client's eyes. */
export type PublicArtifact =
  | {
      readonly kind: 'invoice';
      readonly household: string | null;
      readonly for: string | null;
      /** `FK-42`, or null for one sent before numbering began (ops #76 O2). */
      readonly number: string | null;
      readonly visitDate: string | null;
      readonly issuedOn: string | null;
      readonly dueOn: string | null;
      readonly state: InvoiceState;
      readonly totalCents: number;
      readonly balanceCents: number;
      readonly lines: readonly PublicInvoiceLine[];
      readonly pay: PublicPay;
      readonly feedback: PublicFeedback;
      readonly autopay: PublicAutopay | null;
      readonly charged: PublicCharged | null;
      /**
       * The automatic charge waits until what she told us is put right (ops #72): held for the issue
       * an answer opened, or an issue standing on the clean. A good face that names a missed area
       * opens one, so her face alone no longer says whether her saved method will be charged.
       */
      readonly waitsOnPuttingRight: boolean;
      /** Its PDFs, the invoice's and each receipt's, in the order they were made (ops #76 O8). */
      readonly documents: readonly PublicDocument[];
    }
  | {
      readonly kind: 'visit_report';
      readonly household: string | null;
      readonly for: string | null;
      readonly visitDate: string | null;
      readonly whatWasDone: string;
      readonly clientAskedFor: string | null;
      readonly submittedAt: string;
    };

/**
 * No sign-in. The token is the whole credential and it opens exactly one page. `screen` names the
 * screen of the pay page about to be shown, which the server stamps the first time: a load of the
 * page names `zelle`, and a move forward to the help, the bank screen or the card screen names that
 * one. Moving back names nothing and asks nothing, because a second `zelle` would count another open.
 * `unfolded` names the Zelle screen the first time it is shown after the page loaded with the ways to
 * pay folded away, after an answer below happy: a move, which the server stamps and never counts as an
 * open (ops #54, item 10).
 */
export const openPublic = (
  token: string,
  screen?: PayScreen,
  options: { readonly unfolded?: boolean } = {},
): Promise<PublicArtifact> =>
  request(
    `/api/public/${encodeURIComponent(token)}${query({
      screen,
      unfolded: options.unfolded ? 'true' : undefined,
    })}`,
  );

/**
 * Her link asks for one of Stripe's pages for this one invoice: the bank page or the card page. 200
 * with the page once it exists, 202 while it is being made after the answer has gone, 400 when
 * nothing is owed, 409 while a bank payment on it clears, and 404 for a dead link or a deployment
 * without Stripe. `saveAccount` is her own tick on the bank screen (2026-09-23), sent only when she
 * ticked it, and only for the bank page: the server honours it only while nothing is saved.
 */
export const askForPayPage = (
  token: string,
  page: LinkPayPage,
  saveAccount = false,
): Promise<PaymentPageAnswer> =>
  send(
    `/api/public/${encodeURIComponent(token)}/pay-page`,
    'POST',
    page === 'bank' && saveAccount ? { page, autopay: true } : { page },
  );

/**
 * What a tap on her link came to: `FeedbackRecorded`, and whether the automatic charge waits on putting
 * right what she told us as it stands after the tap, the read's `waitsOnPuttingRight`, so her page words
 * the charge from it without reading itself again (the review of ops #72's fix).
 */
export interface FeedbackRecordedOnLink extends FeedbackRecorded {
  readonly waitsOnPuttingRight: boolean;
}

/**
 * Her answer to "How did we do?", from her invoice's link: 200 with what was written, or
 * `recorded: false` once her link has sent as many as one link may; 422 for a face that is not one
 * of the four or words past their length; 404 for a dead link.
 */
export const tellUsHowItWent = (
  token: string,
  body: FeedbackBody,
): Promise<FeedbackRecordedOnLink> =>
  send(`/api/public/${encodeURIComponent(token)}/feedback`, 'POST', body);

/**
 * Her "I'm paying by Zelle", on an invoice her saved method pays: the charge of that one invoice is
 * paused, once. 200 when the pause stands; 409 `not_charged_automatically`, `charge_in_flight` or
 * `pause_lifted`, each in her words; 400 when nothing is owed; 404 for a dead link.
 */
export const sayPayingByZelle = (
  token: string,
): Promise<{ readonly stage: Extract<HerChargeStage, 'paying_by_zelle'> }> =>
  send(`/api/public/${encodeURIComponent(token)}/paying-by-zelle`, 'POST', {});

/**
 * Her press for one of her invoice's PDFs (ops #76 O8): 200 with a ten-minute link to it, minted now;
 * 409 `document_not_ready` while it is being made; 422 for a document that is not this invoice's; 503
 * where nothing can sign a link; 404 for a dead link. It writes nothing and is not an open.
 */
export const askForDocument = (token: string, documentId: string): Promise<DocumentLinkAnswer> =>
  send(`/api/public/${encodeURIComponent(token)}/document`, 'POST', { documentId });

/* ------------------------------------------------------------------ *
 * The promises. /api/breaches.
 * ------------------------------------------------------------------ */

export interface BreachRow {
  readonly id: string;
  readonly promiseId: string;
  readonly detectedAt: string;
  readonly state: BreachState;
  readonly detail: string | null;
  readonly missedByMinutes: number | null;
  readonly remedy: Remedy | null;
  readonly choiceBelongsTo: ChoiceOwner | null;
  readonly remedyOfferedAt: string | null;
  readonly remedyChosenAt: string | null;
  readonly remedyNote: string | null;
  readonly remedyVisitId: string | null;
  readonly remedyPaymentId: string | null;
  readonly remediedAt: string | null;
  readonly remediedBy: string | null;
  readonly waivedAt: string | null;
  readonly waivedBy: string | null;
  readonly waivedReason: string | null;
  readonly sanctionedReason: string | null;
  readonly createdAt: string;
  readonly code: PromiseCode;
  readonly dueAt: string;
  readonly openedAt: string;
  readonly visitId: string | null;
  readonly visitChangeId: string | null;
  readonly enquiryId: string | null;
  readonly invoiceId: string | null;
  readonly issueId: string | null;
  readonly clientName: string | null;
  readonly visitDate: string | null;
}

/** A live series whose written-ahead visits have run out, or nearly. */
export interface StaleSeries {
  readonly id: string;
  readonly agreementId: string;
  readonly materialisedTo: string | null;
  readonly anchorDate: string;
}

/**
 * A change nobody has resolved, with the household it is about. The client history draws changes
 * under the household they already belong to, so it needs no such thing; a breach list draws them
 * next to each other with nothing else on the screen to say whose home each one is.
 */
export interface UntoldChange extends ChangeRecord {
  /** The day the visit still stands on, because the calendar changes last. */
  readonly scheduledDate: string;
  readonly clientId: string;
  readonly clientName: string;
  readonly town: string;
}

export interface BreachList {
  readonly today: string;
  /** The most this read ever answers with, so a page of them is never mistaken for all of them. */
  readonly limit: number;
  readonly breaches: readonly BreachRow[];
  /** Changes a day old or more with neither `toldAt` nor a typed reason. Rule 7 as a list. */
  readonly untoldChanges: readonly UntoldChange[];
  readonly staleSeries: readonly StaleSeries[];
}

export interface BreachPatch {
  readonly state?: BreachState;
  readonly remedy?: Remedy | null;
  readonly choiceBelongsTo?: ChoiceOwner | null;
  readonly remedyNote?: string | null;
  readonly remedyVisitId?: string | null;
  readonly waivedReason?: string | null;
  readonly sanctionedReason?: string | null;
}

export const listBreaches = (state: BreachState | ''): Promise<BreachList> =>
  request(`/api/breaches${query({ state })}`);

export const patchBreach = (id: string, body: BreachPatch): Promise<{ ok: true }> =>
  send(`/api/breaches/${encodeURIComponent(id)}`, 'PATCH', body);

/* ------------------------------------------------------------------ *
 * Notifications. /api/notifications. Mine, and only mine.
 * ------------------------------------------------------------------ */

export interface NotificationRow {
  readonly id: string;
  readonly userId: string;
  readonly kind: NotificationKind;
  readonly state: NotificationState;
  readonly title: string;
  readonly body: string | null;
  readonly visitId: string | null;
  readonly invoiceId: string | null;
  readonly taskId: string | null;
  readonly promiseBreachId: string | null;
  readonly messageIntentId: string | null;
  readonly readAt: string | null;
  readonly actionedAt: string | null;
  readonly createdAt: string;
}

export const listNotifications = (): Promise<{
  notifications: NotificationRow[];
  unread: number;
}> => request('/api/notifications');

export const markNotificationRead = (id: string): Promise<{ ok: true }> =>
  send(`/api/notifications/${encodeURIComponent(id)}/read`, 'POST', {});

/* ------------------------------------------------------------------ *
 * Enquiries. /api/enquiries. The people who are not clients yet.
 * ------------------------------------------------------------------ */

/**
 * An enquiry as the office list returns it: every column of the row, and the two answers the
 * screen could never work out for itself.
 *
 * `ownerName` is the person who has it, by name, joined on the server from `users`. Without it
 * this screen could name nobody but the person signed in, which is why ownership used to be a
 * "Take it" button and nothing else. `clientId` is the household a booked enquiry became, so a
 * booked row is a link to her rather than a dead end at the word.
 */
export interface EnquiryRow {
  readonly id: string;
  readonly sourceChannel: Channel;
  readonly sourceDetail: string | null;
  /**
   * WHO INTRODUCED HER, as an id. At most one of the two, which the write path refuses to break.
   * Only the client book is offered on a screen; a prospect is set by nothing a person can reach
   * yet, and is shown as "somebody in the outreach ledger" if a row ever carries one.
   */
  readonly referredByClientId: string | null;
  readonly referredByProspectId: string | null;
  /**
   * The id the website minted, on a form lead and on a call that started with a press of the
   * number on the page. It is the proof `website_form` belongs to a row: a row holding one may
   * wear that word and may be given it back, and a row without one may never be typed into it.
   */
  readonly leadId: string | null;
  readonly name: string;
  readonly phoneE164: string | null;
  readonly email: string | null;
  readonly town: string | null;
  readonly zip: string | null;
  readonly serviceLine: ServiceLine | null;
  readonly state: EnquiryState;
  readonly ownerUserId: string | null;
  readonly nextAction: string | null;
  readonly nextActionOn: string | null;
  readonly firstResponseAt: string | null;
  readonly convertedAt: string | null;
  readonly lostReason: string | null;
  /**
   * What held a quote back (ops #86), a word and their words beside it, typed while it is quoted.
   * Optional for a Worker before migration `0048`, which sends neither.
   */
  readonly heldBack?: HeldBackReason | null;
  readonly heldBackNote?: string | null;
  readonly notes: string | null;
  readonly createdAt: string;
  /**
   * Who owns it, by name, and null only when nobody does. Somebody whose own record has since
   * been removed is still named here: she really was the one who took it.
   */
  readonly ownerName: string | null;
  /** The household a booked enquiry became, or null while she is not one and if she never is. */
  readonly clientId: string | null;
  /**
   * The do-not-contact mark in force on her number or her address (ops #44 step two). Optional for
   * a gradual deployment: absent draws no pill.
   */
  readonly doNotContact?: MarksHeld;
  /** The earliest call on it, for "Open the call" (ops #13); null for one typed in or texted. */
  readonly callSid: string | null;
}

export interface EnquiryList {
  /** The server's day. A next action is overdue by this and never by the phone's clock. */
  readonly today: string;
  /** In the server's order: the neglected first. A screen shows it as it came. */
  readonly enquiries: readonly EnquiryRow[];
}

/**
 * Typed in after a call or a conversation. Never `website_form`: a website lead arrives signed
 * with the id the website minted, and the server refuses one typed in by hand.
 *
 * ALMOST EVERYTHING IS OPTIONAL, and the name is the reason. A Thumbtack or Local Services lead
 * that never replied is still a lead both kill rules count, and demanding a name for it means
 * inventing one, so the server names those two itself. Every other channel is asked for a name and
 * refused without one, in the same sentence on the same field.
 */
export interface NewEnquiry {
  readonly sourceChannel: Exclude<Channel, 'website_form'>;
  readonly sourceDetail?: string | null;
  readonly name?: string;
  readonly phoneE164?: string | null;
  readonly email?: string | null;
  readonly town?: string | null;
  readonly zip?: string | null;
  readonly serviceLine?: ServiceLine | null;
  readonly notes?: string | null;
  /** True when the person typing it in has already spoken to the caller. The server stamps when. */
  readonly responded?: boolean;
  /**
   * The day the lead actually arrived, where that is not today: the platform's own lead date,
   * typed in later. The server stamps it at local noon and clamps a day that has not happened yet.
   */
  readonly receivedOn?: string | null;
  readonly referredByClientId?: string | null;
}

export interface EnquiryPatch {
  /** Never `booked`: booking is converting, and the server says so if asked. */
  readonly state?: Exclude<EnquiryState, 'booked'>;
  readonly ownerUserId?: string | null;
  readonly nextAction?: string | null;
  readonly nextActionOn?: string | null;
  readonly notes?: string | null;
  readonly lostReason?: string | null;
  /** Only while it is quoted (ops #86); the server refuses either otherwise. */
  readonly heldBack?: HeldBackReason | null;
  readonly heldBackNote?: string | null;
  readonly serviceLine?: ServiceLine | null;
  readonly name?: string;
  readonly phoneE164?: string | null;
  readonly email?: string | null;
  readonly town?: string | null;
  readonly zip?: string | null;
  /**
   * A SOURCE THAT CAN BE CORRECTED. The whole vocabulary, because a correction has to be able to
   * undo itself; the server is the control, and it refuses `website_form` on any row that does not
   * carry the lead id the website minted.
   */
  readonly sourceChannel?: Channel;
  readonly sourceDetail?: string | null;
  readonly referredByClientId?: string | null;
  readonly referredByProspectId?: string | null;
}

export interface ConvertEnquiry {
  readonly displayName: string;
  readonly contact: {
    readonly displayName: string;
    /** Null falls back to the enquiry's own number and address on the server. */
    readonly phoneE164: string | null;
    readonly email: string | null;
    readonly preferredChannel: PreferredChannel | null;
  };
  readonly site: {
    readonly addressLine1: string;
    readonly addressLine2: string | null;
    readonly town: string;
    readonly postalCode: string;
    readonly county: County;
    readonly bedrooms: number | null;
    readonly bathrooms: number | null;
  };
}

/**
 * Two numbers and no rows, for the digit beside the word in the rail. The server counts them
 * rather than sending the list, because this is asked again on every route change.
 */
export interface EnquiryCounts {
  /** State `new` with no first response stamped. The neglected group, which is the one that stings. */
  readonly noReplyYet: number;
  /** New, contacted or quoted: everything still being worked. */
  readonly open: number;
}

export const countEnquiries = (): Promise<EnquiryCounts> => request('/api/enquiries/summary');

/**
 * FOUR COLUMNS, FOR THE PANEL ON TODAY. The oldest enquiries nobody has replied to, and nothing
 * else about them: that panel drew five names off the whole open list, every column of every open
 * row, on a dashboard already making four other requests.
 *
 * The predicate is the same one `countEnquiries` counts as `noReplyYet`, on the server, so the
 * digit in the rail and the names in the panel can never disagree.
 *
 * `total` is separate from the rows for that reason: the rows stop at the limit, and the digit the
 * panel draws beside its heading has to be how many people are waiting rather than how many names
 * fitted. Seven behind a cap of five is a seven.
 */
export interface AttentionRow {
  readonly id: string;
  readonly name: string;
  readonly createdAt: string;
  readonly sourceChannel: Channel;
}

export interface AttentionPanel {
  readonly today: string;
  readonly total: number;
  readonly enquiries: readonly AttentionRow[];
}

export const listAttentionEnquiries = (limit: number): Promise<AttentionPanel> =>
  request(`/api/enquiries/attention${query({ limit: String(limit) })}`);

/**
 * Every source detail anybody has already typed, from the enquiries and from the client book, so
 * the next one is chosen rather than retyped. Three spellings of one Nextdoor thread are three
 * rows in the roll-up and a kill rule applied to a third of the evidence.
 */
export const listSourceDetails = (): Promise<{ details: string[] }> =>
  request('/api/enquiries/source-details');

/** Open ones by default. `includeClosed` adds booked and lost, which is GET ?all=1. */
export const listEnquiries = (includeClosed: boolean): Promise<EnquiryList> =>
  request(`/api/enquiries${query({ all: includeClosed ? '1' : null })}`);

export const createEnquiry = (body: NewEnquiry): Promise<{ id: string }> =>
  send('/api/enquiries', 'POST', body);

/** Leaving `new` stamps the first reply on the server. The answer says when. */
export const patchEnquiry = (
  id: string,
  body: EnquiryPatch,
): Promise<{ ok: true; firstResponseAt: string | null }> =>
  send(`/api/enquiries/${encodeURIComponent(id)}`, 'PATCH', body);

/**
 * Household, first person and first home in one batch, with the enquiry marked booked in the
 * same write. A duplicate answer names the client that already exists for this enquiry.
 */
export const convertEnquiry = (
  id: string,
  body: ConvertEnquiry,
): Promise<{ clientId: string; duplicate: boolean }> =>
  send(`/api/enquiries/${encodeURIComponent(id)}/convert`, 'POST', body);

/* ------------------------------------------------------------------ *
 * The office follow-ups. /api/tasks, /api/calls, /api/users. Office
 * only on every one of them: a task names a household and carries what
 * the office decided to do about it, which is the client book by
 * another name.
 * ------------------------------------------------------------------ */

/**
 * EVERYBODY WITH A SIGN-IN: the Crew screen's sign-in panel reads it whole, and every picker
 * narrows it first.
 *
 * It answered active office people only while a picker was the only thing reading it, and that is
 * wrong for the panel: a suspended row nobody is shown is an account nobody can reopen, and the
 * sign-in this business most needs to shut belongs to a cleaner who has left. So the read carries
 * the cleaners and the suspended accounts, and `officePeople` in `web/src/people.ts` is what a
 * picker applies to it. Hiding a name is UX and never the control; `api/src/lib/office.ts` refuses
 * the same people at the doors whatever any screen draws.
 *
 * `status` is OPTIONAL on purpose. A read that does not carry the column must not be turned into a
 * claim by the screen reading it: absent is read as active, which is what the schema defaults it
 * to, and no line then says anything about a suspension in either direction.
 */
export interface OfficeUser {
  readonly id: string;
  readonly displayName: string;
  readonly role: Role;
  readonly status?: UserStatus;
}

export const listUsers = (): Promise<{ users: readonly OfficeUser[] }> => request('/api/users');

/**
 * SHUTTING A DOOR, WHICH IS NOT REMOVING A PERSON. Owners only, and the server is what enforces
 * that. `api/src/middleware/identity.ts` reads `users.status` on every request and refuses
 * anything but `active`, so this takes effect at her next request rather than at the expiry of a
 * session that is long on purpose. Nothing is deleted and the column can be set back.
 */
export const setUserStatus = (
  id: string,
  status: UserStatus,
): Promise<{ ok: true; id: string; status: UserStatus }> =>
  send(`/api/users/${encodeURIComponent(id)}`, 'PATCH', { status });

/**
 * One follow-up. Every column of the row, plus the household's name and the name of the person it
 * was given to, both joined on the server so a list of thirty does not cost sixty more requests.
 */
export interface TaskRow {
  readonly id: string;
  readonly kind: TaskKind;
  readonly state: TaskState;
  readonly title: string;
  readonly detail: string | null;
  readonly dueOn: string | null;
  readonly assignedTo: string | null;
  readonly assignedToName: string | null;
  readonly clientId: string | null;
  readonly clientName: string | null;
  readonly policyId: string | null;
  readonly promiseBreachId: string | null;
  /**
   * The invoice it is about, when an answer to "How did we do?" raised it, or an automatic charge
   * that did not go through, which the office answers by sending the invoice from its screen (ops #46).
   */
  readonly invoiceId: string | null;
  /**
   * The ask from home it was opened for (ops #19, step 7), which the screen opens so the office can
   * decide it, or null for a follow-up about anything else.
   */
  readonly homeRequestId: string | null;
  /**
   * THE ENQUIRY A QUOTE'S FOLLOW-UP NAMES (ops #86), which the screen opens, with its number and
   * address and the marks on them read again as the list was shown, so a call nobody may make is
   * drawn as one. Optional for a Worker before migration `0048`.
   */
  readonly enquiryId?: string | null;
  readonly enquiry?: {
    readonly id: string;
    readonly name: string;
    readonly state: EnquiryState | null;
    readonly phoneE164: string | null;
    readonly email: string | null;
    readonly doNotContact: MarksHeld;
  } | null;
  readonly completedAt: string | null;
  readonly completedBy: string | null;
  readonly createdBy: string | null;
  readonly createdAt: string;
  readonly updatedAt: string;
  readonly deletedAt: string | null;
}

/** Which rows were asked for. `open` is what the door answers when nothing is asked. */
export type TaskFilter = 'open' | 'done' | 'all';

export interface TaskList {
  /** The server's day. A due date is late by this and never by the phone's clock. */
  readonly today: string;
  readonly state: TaskFilter;
  /** The most the door will answer with. A list at the cap is a list nobody is reading. */
  readonly limit: number;
  readonly tasks: readonly TaskRow[];
}

/** Two numbers and no rows, for the digit beside the word in the rail. */
export interface TaskCounts {
  readonly open: number;
  /** Open, with a day, and that day is behind the server's own. Today is not late. */
  readonly overdue: number;
}

export interface NewTask {
  readonly kind: TaskKind;
  readonly title: string;
  readonly detail?: string | null;
  readonly dueOn?: string | null;
  readonly assignedTo?: string | null;
  readonly clientId?: string | null;
}

/**
 * Every field again and all of them optional, because a follow-up is corrected one at a time. A
 * field left out is left alone; a nullable field sent as null is cleared, which is how a task is
 * taken off somebody rather than passed to nobody in particular.
 */
export interface TaskPatch {
  readonly title?: string;
  readonly detail?: string | null;
  readonly dueOn?: string | null;
  readonly assignedTo?: string | null;
  /** Only these two. `doing` and `cancelled` are in the vocabulary and nothing writes either. */
  readonly state?: Extract<TaskState, 'open' | 'done'>;
  /**
   * Done on a quote's follow-up (ops #86): which went, the call and the email, and what came of it,
   * which the server requires whenever one is ticked. A touch is written for each.
   */
  readonly channels?: readonly FollowUpChannel[];
  readonly outcome?: TouchOutcome;
}

export const listTasks = (state: TaskFilter): Promise<TaskList> =>
  request(`/api/tasks${query({ state })}`);

export const countTasks = (): Promise<TaskCounts> => request('/api/tasks/summary');

export const createTask = (body: NewTask): Promise<{ id: string }> =>
  send('/api/tasks', 'POST', body);

export const patchTask = (id: string, body: TaskPatch): Promise<{ ok: true }> =>
  send(`/api/tasks/${encodeURIComponent(id)}`, 'PATCH', body);

/** Soft delete with a reason, the same shape and the same words as the enquiry discard. */
export const discardTask = (id: string, body: Discard): Promise<{ ok: true }> =>
  send(`/api/tasks/${encodeURIComponent(id)}`, 'DELETE', body);

/** One step of a quote's follow-up (ops #86), as the enquiry's panel lists it. */
export interface FollowUpStep {
  readonly id: string;
  readonly round: number;
  readonly day: 1 | 2 | 3 | null;
  readonly title: string;
  readonly state: TaskState;
  readonly dueOn: string | null;
  readonly completedAt: string | null;
}

/** What was recorded as a step was done: a call or an email, and what came of it. Never its words. */
export interface FollowUpTouch {
  readonly id: string;
  readonly channel: string;
  readonly touchNumber: number | null;
  readonly templateName: string | null;
  readonly outcome: TouchOutcome;
  readonly occurredAt: string;
}

export const getFollowUps = (
  enquiryId: string,
): Promise<{ steps: readonly FollowUpStep[]; touches: readonly FollowUpTouch[] }> =>
  request(`/api/enquiries/${encodeURIComponent(enquiryId)}/follow-ups`);

/**
 * WHO RANG, across the whole client book rather than one household at a time.
 *
 * `hasTranscript` is a yes or a no and never the words: the transcript itself stays on the
 * household's own page, where whoever reads it is already looking at whose call it was, and the
 * audio never leaves the phone system at all.
 */
export interface CallRow {
  readonly id: string;
  readonly clientId: string;
  readonly clientName: string;
  readonly contactId: string | null;
  readonly contactName: string | null;
  readonly callSid: string;
  readonly direction: TouchDirection;
  readonly outcome: CallOutcome | null;
  readonly occurredAt: string;
  readonly endedAt: string | null;
  readonly durationSeconds: number | null;
  readonly hasTranscript: boolean;
  /**
   * The do-not-contact mark in force on the person whose number matched (ops #44 step two).
   * Optional for a gradual deployment: absent draws no pill.
   */
  readonly doNotContact?: MarksHeld;
}

export interface CallList {
  readonly from: string;
  readonly to: string;
  readonly limit: number;
  readonly calls: readonly CallRow[];
}

/** Two days, both ends included. The server answers newest first and caps the page. */
export const listCalls = (from: string, to: string): Promise<CallList> =>
  request(`/api/calls${query({ from, to })}`);

/* ------------------------------------------------------------------ *
 * One call, as the phone system tells it (ops #13, `docs/seams/phone.md`
 * section 7). The owners' and the managers' only, on the server.
 * ------------------------------------------------------------------ */

/** What ops holds of the call: a household's call row, or the enquiry a call opened. */
export interface OpsCall {
  readonly kind: 'household' | 'enquiry';
  readonly clientId: string | null;
  readonly clientName: string | null;
  readonly contactName: string | null;
  readonly enquiryId: string | null;
  readonly enquiryName: string | null;
  readonly direction: TouchDirection | null;
  readonly outcome: CallOutcome | null;
  readonly occurredAt: string | null;
  readonly endedAt: string | null;
  readonly durationSeconds: number | null;
  /** The voicemail transcript ops appended to a household's call. */
  readonly notes: string | null;
}

/** One fact that reached ops about the call, by the receipt ops answered it with. */
export interface OpsReceipt {
  readonly id: string;
  readonly type: string;
  readonly receivedAt: string;
  readonly error: string | null;
}

export interface PhoneMoment {
  readonly at: string | null;
  readonly elapsed: number | null;
  readonly title: string | null;
  readonly detail: string | null;
  /** `line`, `owner`, `recording`, `note`, or a word the phone system adds later. */
  readonly actor: string | null;
}

export interface PhoneFactLine {
  readonly type: string | null;
  readonly occurredAt: string | null;
  readonly remoteId: string | null;
  readonly confirmedAt: string | null;
}

/**
 * The phone system's answer, read by name on the server. The names in it, the phone that answered,
 * the phones and the crew in the moments and who listened, are personal data of the people on the
 * line: drawn on the page, and never kept anywhere by this screen.
 */
export interface PhoneCallAnswer {
  readonly callSid: string | null;
  readonly startedAt: string | null;
  readonly direction: string | null;
  /** The phone system's own word, `dial_out` included. */
  readonly outcome: string | null;
  readonly durationSeconds: number | null;
  readonly callerLastFour: string | null;
  readonly answeredBy: string | null;
  readonly notifiedAt: string | null;
  readonly acknowledged: boolean | null;
  readonly transcript: string | null;
  readonly redacted: readonly string[];
  readonly moments: readonly PhoneMoment[];
  readonly facts: readonly PhoneFactLine[];
  readonly audio: {
    readonly stored: boolean | null;
    readonly seconds: number | null;
    /** Only ever the phone system's own page, where a person signs in and listens. */
    readonly page: string | null;
    readonly pageWithheld: boolean;
  } | null;
}

/** Every state but the three that carry no answer has when it came, and when Ask again may read. */
interface Answered {
  readonly at: string;
  /** Seconds until the server will read again, by its one rule. */
  readonly askAgainInSeconds: number;
}

/** What a door says of the phone system. */
export type PhoneView =
  | { readonly state: 'not_configured' }
  /** A version preview, where the server never asks the phone system. */
  | { readonly state: 'preview' }
  | { readonly state: 'asked' }
  | { readonly state: 'nothing_yet' }
  | (Answered & {
      readonly state: 'answered';
      readonly call: PhoneCallAnswer;
      readonly numberCheck: 'same' | 'different' | null;
      readonly receiptsCheck: {
        readonly unknownToOps: readonly string[];
        readonly unnamedByPhone: number;
      };
    })
  | (Answered & { readonly state: 'not_found' })
  | (Answered & { readonly state: 'forgotten'; readonly forgottenAt: string | null })
  | (Answered & {
      readonly state: 'unavailable';
      readonly code: string;
      readonly message: string | null;
    })
  | (Answered & {
      readonly state: 'refused';
      readonly status: number;
      readonly code: string;
      readonly message: string | null;
    })
  | (Answered & {
      readonly state: 'unreachable';
      readonly status: number | null;
      readonly reason: 'timeout' | 'network' | 'status' | 'shape';
      readonly message: string | null;
    });

export interface CallPage {
  readonly callSid: string;
  readonly call: OpsCall;
  readonly receipts: readonly OpsReceipt[];
  readonly phone: PhoneView;
}

/** The first view: ops's own facts at once, and the phone asked behind the answer. */
export const getCall = (callSid: string): Promise<CallPage> =>
  request(`/api/calls/${encodeURIComponent(callSid)}`);

/** The page's poll, which never makes the server read. */
export const pollCall = (callSid: string): Promise<{ readonly phone: PhoneView }> =>
  request(`/api/calls/${encodeURIComponent(callSid)}/phone`);

/** "Ask the phone system again", which reads only once the last answer's minute is up. */
export const askPhoneAgain = (callSid: string): Promise<{ readonly phone: PhoneView }> =>
  request(`/api/calls/${encodeURIComponent(callSid)}/phone`, { method: 'POST' });

/**
 * ONE LINE OF THE "RINGING NOW" BANNER (ops #84 phase 4): a call on the line now, named by the person
 * and the household or by the enquiry, never by its whole number, with where Open lands.
 */
export interface LiveCall {
  readonly callSid: string;
  readonly startedAt: string;
  /** `outbound` is a call the office placed, "On the line" rather than "Ringing now". */
  readonly direction: 'inbound' | 'outbound';
  readonly kind: 'client' | 'enquiry';
  readonly name: string;
  readonly household: string | null;
  /** An address in this application: the household's page, or the Enquiries screen with the row open. */
  readonly open: string;
}

/** Who is on the line now: the banner's poll, office only on the server. */
export const listLiveCalls = (): Promise<{ readonly calls: readonly LiveCall[] }> =>
  request('/api/calls/live');

/**
 * WHO TEXTED (ops #25): the texts nobody has handled yet, newest first, a hundred at most.
 *
 * `body` is what she wrote, exactly as it came, and it is drawn as text and never as markup: a
 * stranger typed it. Null when a text came with no words, or once she asked to be forgotten, which
 * blanks `fromE164` too. `mediaCount` says how many pictures there were and nothing about them,
 * because the pictures stay with the phone system; `redacted` names the kinds its scrub took out.
 * `phonePage` is the phone system's page for the text, behind its own sign-in, where the pictures
 * are (faithfully-kept-phone#17), and null where this deployment names none.
 *
 * And what the actions on it need (ops #26): `doNotContact`, the marks on her and on the number it
 * came from, of which the one on calls decides whether the screen offers a call; and `attachTo`, for
 * a text that landed on nobody, the people and the open enquiries whose number it is, and empty for
 * every other text.
 */
export interface MessageRow {
  readonly id: string;
  readonly phoneMessageId: string;
  readonly fromE164: string | null;
  readonly body: string | null;
  readonly redacted: readonly string[];
  readonly mediaCount: number;
  readonly keyword: InboundKeyword | null;
  readonly receivedAt: string;
  readonly handled: InboundMessageState;
  readonly clientId: string | null;
  readonly clientName: string | null;
  readonly contactId: string | null;
  readonly contactName: string | null;
  readonly enquiryId: string | null;
  readonly enquiryName: string | null;
  readonly taskId: string | null;
  /**
   * The open hold this text opened, a stop in her words waiting for the office (ops #30), or null.
   * Optional for a gradual deployment: absent draws nothing.
   */
  readonly holdId?: string | null;
  readonly phonePage: string | null;
  readonly doNotContact: MarksHeld;
  readonly attachTo: readonly AttachChoice[];
}

/** Somebody a text on nobody may be put on: a person on a household, or an open enquiry. */
export type AttachChoice =
  | {
      readonly kind: 'contact';
      readonly contactId: string;
      readonly contactName: string;
      readonly clientId: string;
      readonly clientName: string;
    }
  | { readonly kind: 'enquiry'; readonly enquiryId: string; readonly enquiryName: string };

export interface MessageList {
  readonly limit: number;
  readonly messages: readonly MessageRow[];
}

/** The open texts, newest first. */
export const listMessages = (): Promise<MessageList> => request('/api/messages');

/** Who a text goes on: one person, who takes her household with her, or one open enquiry. */
export type AttachTarget = { readonly contactId: string } | { readonly enquiryId: string };

/** Puts a text that landed on nobody on somebody's record, once (ops #26). */
export const attachMessage = (
  id: string,
  target: AttachTarget,
): Promise<{
  ok: true;
  clientId: string | null;
  contactId: string | null;
  enquiryId: string | null;
}> => send(`/api/messages/${encodeURIComponent(id)}/attach`, 'POST', target);

/** Takes a text off the open list as handled or as spam, once, with her name and the moment. */
export const closeMessage = (
  id: string,
  as: 'handled' | 'spam',
): Promise<{ ok: true; handled: 'handled' | 'spam'; handledAt: string }> =>
  send(`/api/messages/${encodeURIComponent(id)}/${as}`, 'POST', {});

/** A visit an issue from a text may be opened on: her household's, up to today, newest first. */
export interface MessageVisit {
  readonly id: string;
  readonly scheduledDate: string;
  readonly status: VisitStatus;
  readonly kind: string;
}

export const listMessageVisits = (
  id: string,
): Promise<{ readonly visits: readonly MessageVisit[] }> =>
  request(`/api/messages/${encodeURIComponent(id)}/visits`);

/** An issue on one of those visits, found by her unless the office says not, naming the text. */
export const openIssueFromMessage = (
  id: string,
  body: NewIssue & { readonly visitId: string },
): Promise<{ id: string }> => send(`/api/messages/${encodeURIComponent(id)}/issues`, 'POST', body);

/**
 * A TEXT SHE SENT, in a thread (ops #26, part 2). `at` is when the phone system says it arrived.
 * `body` and `fromE164` are null once a forget blanked them, and `body` where a text came with no
 * words; `contactName` names the person on the household whose number it is, and is null on an
 * enquiry's thread, where the text is hers by the enquiry's own name.
 */
export interface ThreadTextIn {
  readonly direction: 'in';
  readonly id: string;
  readonly at: string;
  readonly phoneMessageId: string;
  readonly fromE164: string | null;
  readonly body: string | null;
  readonly redacted: readonly string[];
  readonly mediaCount: number;
  readonly keyword: InboundKeyword | null;
  readonly handled: InboundMessageState;
  readonly contactId: string | null;
  readonly contactName: string | null;
  readonly phonePage: string | null;
  /**
   * The hold this text opened (ops #30), open or however it ended, or null. Optional for a gradual
   * deployment: absent draws nothing.
   */
  readonly hold?: StopHoldView | null;
}

/**
 * A TEXT OPS COMPOSED TO ONE OF HER PEOPLE, in a thread. `at` is when it was written. `body` is what
 * was handed over, which is what she read, or what was approved where nothing was handed over yet.
 * The rest is what the visit's page reads of a message to say where it stands and why it did not go.
 */
export interface ThreadTextOut {
  readonly direction: 'out';
  readonly id: string;
  readonly at: string;
  readonly contactId: string;
  readonly contactName: string | null;
  readonly body: string;
  readonly state: MessageIntentState;
  readonly failureCode: FailureCode | null;
  readonly handedOffAt: string | null;
  readonly refusedAt: string | null;
  readonly cancelledAt: string | null;
  readonly sendAfter: string | null;
  /**
   * The text in it answers, by the phone system's id, which a text in carries as `phoneMessageId`
   * (ops #32); null for a text that answers none. Optional because a gradual deployment splits code:
   * absent, from a Worker older than the field, pairs with nothing.
   */
  readonly inReplyTo?: string | null;
  /**
   * For a text that failed because the phone system holds her number (ops #33), where the newest
   * release of it stands, or null while nobody has confirmed the number; null on every other text.
   * Optional for a gradual deployment: absent draws the button alone.
   */
  readonly release?: ReleaseView | null;
}

/**
 * WHERE A RELEASE OF A HELD NUMBER STANDS (ops #33): asked and waiting, released, refused because her
 * STOP stood there, parked before or after it went, or taken with an answer that did not say.
 */
export type ReleaseState = 'asked' | 'released' | 'refused_stop' | 'parked' | 'unclear';

export interface ReleaseView {
  readonly id: string;
  readonly state: ReleaseState;
  readonly askedBy: string | null;
  readonly askedByName: string | null;
  /** The day the number was confirmed, in Florida. */
  readonly askedOn: string;
  /** The day the phone system answered, or ops parked it, in Florida; null while it waits. */
  readonly answeredOn: string | null;
  /** Why a parked release did not go: the register's words, or the phone system's. */
  readonly error: string | null;
  /** Whether a new press asks again, by the rule the server writes by. */
  readonly mayAskAgain: boolean;
}

/** What Number confirmed answers: a release written, or one already asked and where it stands. */
export type NumberConfirmed =
  | { readonly written: true; readonly releaseId: string }
  | {
      readonly written: false;
      readonly releaseId: string;
      readonly state: ReleaseState;
      readonly message: string;
    };

/**
 * NUMBER CONFIRMED (ops #33): the office called her and the number is right, so the phone system is
 * asked to release it. Nothing is sent to her. Office only; refused while ops would refuse her a text.
 */
export const confirmNumber = (
  clientId: string,
  contactId: string,
  messageIntentId: string,
): Promise<NumberConfirmed> =>
  send(
    `/api/clients/${encodeURIComponent(clientId)}/contacts/${encodeURIComponent(contactId)}/release-number`,
    'POST',
    { messageIntentId },
  );

export type ThreadText = ThreadTextIn | ThreadTextOut;

/** A page of a thread: the newest `limit` texts, oldest first, and `more` when older ones are kept. */
export interface TextThreadPage {
  readonly limit: number;
  readonly more: boolean;
  readonly texts: readonly ThreadText[];
}

/** The texts with one household, in and out, or the texts on one enquiry. Office only. */
export const getTextThread = (of: {
  readonly kind: 'client' | 'enquiry';
  readonly id: string;
}): Promise<TextThreadPage> =>
  request(`/api/messages/thread/${of.kind}/${encodeURIComponent(of.id)}`);

/**
 * WHAT THE COMPOSER NEEDS BEFORE ANYBODY TYPES (ops #32), for one person on a household: her, how the
 * signed-in person signs a text, where the register stands for texts and the open hold that refuses
 * them (ops #30), the go-live switch on her number, whether the opt-in confirmation goes ahead, how
 * many of the three a day her number has used, the texting hours, and her household's visits a week
 * back and two ahead. The sentences in `texting.refusal` and `switch.refusal` are the server's own,
 * which the screen says in plain words (`Composer.tsx`). Every one of these is UX: the door asks
 * them all again, and refuses in words.
 */
export interface ComposeContext {
  readonly contact: {
    readonly id: string;
    readonly name: string;
    readonly phoneE164: string | null;
    readonly clientId: string;
    readonly clientName: string;
  };
  /** "Faithfully Kept, this is <first name>: ", as the campaign filed a reply. */
  readonly signature: string;
  readonly texting: {
    readonly allowed: boolean;
    readonly refusal: string | null;
    /** The open hold on a stop in her own words that refuses her (ops #30), or null. */
    readonly holdId: string | null;
    /** The do-not-contact mark that refuses her (ops #44), or null. */
    readonly markId: string | null;
  };
  readonly switch: { readonly refusal: string | null };
  /**
   * Whether the phone system holds her number since a text of ours to it failed `number_held`
   * (ops #33): `held` while nobody has asked for a release since, `asked` while one waits to go, and
   * null, or absent from a Worker older than the field, where nothing holds it.
   */
  readonly numberHeld?: 'held' | 'asked' | null;
  readonly confirmationNeeded: boolean;
  readonly counted: { readonly inLast24h: number; readonly cap: number };
  /** Whether texts go now, 8am to 8pm in Florida, and the next opening as an instant when not. */
  readonly hours: { readonly open: boolean; readonly opensAt: string | null };
  readonly visits: readonly MessageVisit[];
  /**
   * What her choices would do to a text of each kind the composer offers (ops #76 phase 2), at every
   * home. Absent from a Worker older than the field.
   */
  readonly choices?: Partial<Record<string, ChannelPreview>>;
}

export const getComposeContext = (contactId: string): Promise<ComposeContext> =>
  request(`/api/messages/compose/${encodeURIComponent(contactId)}`);

/**
 * WHAT THE COMPOSER NEEDS BEFORE AN EMAIL IS TYPED (ops #84 phase 5): the person and their address as
 * on file (null where there is none), the first name home shows as who wrote it, the register for
 * email with the mark that refuses them, the switch for client email on their address, and the
 * household's visits a week back and two ahead, whose day a booking's or a finished clean's subject
 * names. UX only: the door asks it all again and refuses in words.
 */
export interface EmailComposeContext {
  readonly channel: 'email';
  readonly contact: {
    readonly id: string;
    readonly name: string;
    readonly email: string | null;
    readonly clientId: string;
    readonly clientName: string;
  };
  readonly author: string | null;
  readonly emailing: {
    readonly allowed: boolean;
    readonly refusal: string | null;
    readonly markId: string | null;
  };
  readonly switch: { readonly state: EmailGoLiveState; readonly refusal: string | null };
  readonly visits: readonly MessageVisit[];
  /** And to an email of each kind (ops #76 phase 2). Absent from a Worker older than the field. */
  readonly choices?: Partial<Record<string, ChannelPreview>>;
}

export const getEmailComposeContext = (contactId: string): Promise<EmailComposeContext> =>
  request(`/api/messages/compose/${encodeURIComponent(contactId)}?channel=email`);

/**
 * WHAT THE CLIENT READS OF A FINISHED CLEAN, FOR MESSAGE 11 (ops #98 phase 4,
 * `docs/plan-client-language.md` section 6), read once a visit is chosen for `visit_finished`: the
 * household's language, the office's words for the client of the report with the language they were
 * written in, null until the office has written them, which the message waits for; and, for an English
 * household whose words are English and whose visit names its cleaner, message 11's approved email
 * sentences filled by the server with the version they were copied at, null otherwise. Never the
 * cleaner's own words. UX only: the door asks the gate again.
 *
 * `photoReport` (ops #86 phase 4, the business's `.10`): where the household allows photos and a live
 * photo of the visit stands for one of the things they check first, message 34 rides inside 11, and
 * this names those things in the household's own lines and whether the words carry their page at home
 * (the question's four faces and "See the report and photos"), which only a live `client` door opens.
 * Null where 11 goes alone, as before.
 */
export interface FinishedWords {
  readonly visitId: string;
  readonly householdLanguage: ClientLanguage;
  readonly forClient: {
    readonly whatWasDone: string;
    readonly language: ClientLanguage | null;
  } | null;
  readonly approved: {
    readonly version: string;
    readonly email: string;
    readonly photoReport: { readonly lines: readonly string[]; readonly linked: boolean } | null;
  } | null;
  readonly cleanerNamed: boolean;
}

export const getFinishedWords = (contactId: string, visitId: string): Promise<FinishedWords> =>
  request(
    `/api/messages/compose/${encodeURIComponent(contactId)}/finished/${encodeURIComponent(visitId)}`,
  );

/**
 * One message the office writes: to whom, which kind, its words, the text in it answers, and the
 * channel, a text unless it says `email` (ops #84 phase 5). An email answers no text, and its subject
 * is fixed by its kind, never typed.
 */
export interface NewText {
  readonly contactId: string;
  readonly kind: ComposerKind;
  readonly channel?: 'sms' | 'email';
  readonly body: string;
  /** The text it answers, by its ops id, `inbound_messages.id`; absent for a text that answers none. */
  readonly inReplyTo?: string;
  readonly visitId?: string;
}

/**
 * What the door wrote: the intent, whether the confirmation goes ahead, whether her text is handled,
 * and for an email the channel and whether it waits for their door at home.
 */
export interface ComposedText {
  readonly intentId: string;
  readonly confirmationAhead: boolean;
  readonly handled: boolean;
  readonly channel?: 'email';
  readonly waitsForHerDoor?: boolean;
}

/**
 * Writes one text for the phone system to deliver, or refuses in words: `claim_not_allowed` and
 * `price_not_allowed` carry `findings` in the refusal's body (`ClaimsFinding` in `@fk/shared`).
 */
export const composeText = (text: NewText): Promise<ComposedText> =>
  send('/api/messages/compose', 'POST', text);

/* ------------------------------------------------------------------ *
 * Report A: where households came from. Office and owner only, and
 * there is no write in this section at all.
 * ------------------------------------------------------------------ */

/** The eight numbers a channel answers with, over the window that was asked for. */
export interface ChannelReportRow {
  readonly channel: Channel;
  readonly leads: number;
  readonly booked: number;
  readonly households: number;
  readonly recurring: number;
  /** Marketing spend in the window, and for referral the rewards owed or paid in it. */
  readonly costCents: number;
  readonly reviewsAsked: number;
  readonly reviewsGiven: number;
  readonly ended: number;
  /**
   * What the channel's households paid in the window, net of refunds, never a tip (ops #86). Optional
   * for a Worker before phase 3.
   */
  readonly revenueCents?: number;
  /**
   * On an owner's row alone (ops #86): the cleaner pay of the channel's cleans done in the window,
   * what is left after it, and the done cleans with no rate on file. Absent on a manager's row, and
   * the screen draws nothing it was not sent.
   */
  readonly cleanerPayCents?: number;
  readonly afterPayCents?: number;
  readonly unpricedCleans?: number;
}

/** What the plan budgets for one new recurring household on this channel, in whole cents. */
export interface PlanningRange {
  readonly channel: Channel;
  readonly lowCents: number;
  readonly highCents: number;
}

/**
 * The whole planning entry, which the per-channel report carries and the channel-level one does
 * not. `row`, `costPerRecurring` and `killOrCap` are the cells of `first-20-clients.md` section 2
 * quoted character for character; `note` is anything ops added in its own words.
 */
export interface PlanningFigure {
  readonly channel: Channel;
  readonly lowCents: number | null;
  readonly highCents: number | null;
  readonly row: string | null;
  readonly costPerRecurring: string | null;
  readonly killOrCap: string | null;
  readonly note: string | null;
}

export interface SourcesReport {
  readonly from: string;
  readonly to: string;
  readonly channels: readonly ChannelReportRow[];
  readonly planning: readonly PlanningRange[];
  /**
   * The day the figures were copied out of the business repository, and where from. It travels
   * with them so the screen never holds a third copy of a fact it cannot check.
   */
  readonly planningReadOn: string;
  readonly planningSource: string;
}

/** One channel broken down by the words somebody typed. No cost: expenses carry no detail. */
export interface SourceDetailRow {
  readonly detail: string | null;
  readonly leads: number;
  readonly booked: number;
  readonly households: number;
  readonly recurring: number;
  readonly reviewsAsked: number;
  readonly reviewsGiven: number;
  readonly ended: number;
}

/** Who introduced whom, on the referral channel and on no other. */
export interface IntroducerRow {
  readonly referrerName: string | null;
  readonly clientName: string;
  readonly cohortDate: string;
}

export interface ChannelReport {
  readonly from: string;
  readonly to: string;
  readonly channel: Channel;
  readonly costCents: number;
  readonly planning: PlanningFigure | null;
  readonly planningReadOn: string;
  readonly planningSource: string;
  readonly details: readonly SourceDetailRow[];
  readonly introducers: readonly IntroducerRow[];
  /**
   * What held the channel's open quotes of the window back (ops #86), most first, null for those
   * with no word yet. Optional for a Worker before migration `0048`.
   */
  readonly heldBack?: readonly {
    readonly reason: HeldBackReason | null;
    readonly quotes: number;
  }[];
}

/** Both ends of the window are days, both are included, and both are the caller's to choose. */
export const getSourcesReport = (from: string, to: string): Promise<SourcesReport> =>
  request(`/api/reports/sources${query({ from, to })}`);

/**
 * The roll-up for one channel, asked for only when somebody opens one. Two small calls beat one
 * big one here: the first screen is drawn from eight statements whatever the vocabulary grows to.
 */
export const getChannelReport = (
  channel: Channel,
  from: string,
  to: string,
): Promise<ChannelReport> =>
  request(`/api/reports/sources/${encodeURIComponent(channel)}${query({ from, to })}`);

/* ------------------------------------------------------------------ *
 * What the business spent. Office and owner only, four doors, and
 * every one of them audited on the server.
 * ------------------------------------------------------------------ */

/**
 * One row of money going out. Only the columns the spend screen reads are named: `created_at` and
 * the soft-delete stamp are the server's business, and a discarded row never arrives here at all.
 */
export interface ExpenseRow {
  readonly id: string;
  readonly incurredOn: string;
  readonly paidOn: string | null;
  readonly category: ExpenseCategory;
  readonly vendor: string;
  readonly description: string;
  readonly amountCents: number;
  /** Null where the spend buys no channel at all, which is most of what this business spends. */
  readonly channel: Channel | null;
  readonly notes: string | null;
  /**
   * The campaign the money paid for, or null on most rows. It can name a campaign thrown away after
   * it was paid for, which the register no longer sends: the money was spent on it either way.
   */
  readonly campaignId: string | null;
  /**
   * Growth's key for a day of spend it sent, `<campaign>:<day>` or with `:fal` after it, and null on
   * every row the office typed (ops #47). A row carrying one is growth's: the server refuses every
   * correction to it but `paidOn` and `notes`, and refuses to throw it away.
   */
  readonly growthSpendKey: string | null;
}

/** A page of spend. `limit` is the server's cap, said out loud so a full page can say so. */
export interface ExpenseList {
  readonly from: string;
  readonly to: string;
  readonly limit: number;
  readonly expenses: readonly ExpenseRow[];
}

export interface NewExpense {
  readonly incurredOn: string;
  readonly paidOn: string | null;
  readonly category: ExpenseCategory;
  readonly vendor: string;
  readonly description: string;
  readonly amountCents: number;
  readonly channel: Channel | null;
  readonly notes: string | null;
  /** A campaign the office still holds, or null. The server refuses one thrown away. */
  readonly campaignId: string | null;
}

/**
 * A correction. Every field is optional, and a nullable one sent as null is cleared, which is how
 * a channel typed on the wrong row comes off it rather than staying there for ever.
 *
 * `campaignId` left out is left alone, and the server asks whether a campaign is still held only
 * when one is sent: so a row naming a campaign thrown away after it was paid for still takes a
 * correction to its amount, as long as that campaign is not sent again.
 *
 * On a day of spend growth sent the server takes `paidOn` and `notes` and nothing else.
 */
export interface ExpensePatch {
  readonly incurredOn?: string;
  readonly paidOn?: string | null;
  readonly category?: ExpenseCategory;
  readonly vendor?: string;
  readonly description?: string;
  readonly amountCents?: number;
  readonly channel?: Channel | null;
  readonly notes?: string | null;
  readonly campaignId?: string | null;
}

/** Newest first by the day the money was spent, which is the column every report counts on. */
export const listExpenses = (from: string, to: string): Promise<ExpenseList> =>
  request(`/api/expenses${query({ from, to })}`);

export const createExpense = (body: NewExpense): Promise<{ id: string }> =>
  send('/api/expenses', 'POST', body);

export const patchExpense = (id: string, body: ExpensePatch): Promise<{ ok: true }> =>
  send(`/api/expenses/${encodeURIComponent(id)}`, 'PATCH', body);

/** Soft delete with a reason, the same shape and the same words as the enquiry discard. */
export const discardExpense = (id: string, body: Discard): Promise<{ ok: true }> =>
  send(`/api/expenses/${encodeURIComponent(id)}`, 'DELETE', body);

/* ------------------------------------------------------------------ *
 * The campaigns register, and what each campaign came to. Office and
 * owner only: three writes, every one audited, and one report.
 * ------------------------------------------------------------------ */

/**
 * One campaign with its figures, as `GET /api/reports/campaigns` sends it. The figures are the
 * server's: the live spend naming the campaign, and the enquiries and households carrying its word
 * on its channel inside its days and the fourteen after them, the word being the website's campaign
 * word or the source detail typed exactly (ops #47). Only the columns the campaigns screen reads
 * are named.
 */
export interface CampaignLine {
  readonly id: string;
  readonly name: string;
  readonly channel: Channel;
  readonly platform: string | null;
  readonly plannedBudgetCents: number;
  readonly startsOn: string;
  /** Null while nobody has decided, and then it counts through today. */
  readonly endsOn: string | null;
  readonly sourceDetail: string;
  readonly status: CampaignStatus;
  readonly notes: string | null;
  /**
   * Growth's key for a campaign it filed, and null on every campaign the office typed (ops #47). A
   * campaign carrying one is growth's: the server refuses every correction to it but its notes, and
   * refuses to throw it away.
   */
  readonly growthCampaignId: string | null;
  readonly spendCents: number;
  readonly leads: number;
  /** Of those leads, the ones that booked: in state booked, or carrying a conversion stamp. */
  readonly booked: number;
  readonly households: number;
  /** Of those households, the ones with an agreement on a repeating cadence that was reached. */
  readonly recurring: number;
  /** Of those households, the ones off the book, whatever the reason. */
  readonly ended: number;
  /**
   * What every household it brought has paid, refunds taken off and tips left out (ops #47, step
   * 3). Drawn from all of its households, so it is never divided by `households`.
   */
  readonly revenueCents: number;
  /** Null where no household came, which is not the same as costing nothing. */
  readonly costPerHouseholdCents: number | null;
  /** Null where no household recurred, the same way. */
  readonly costPerRecurringHouseholdCents: number | null;
  /** The days counted, both ends included, or null for a campaign that has not started. */
  readonly counted: { readonly from: string; readonly to: string } | null;
  /** Enquiries carrying its campaign word on another channel: beside its count, never in it. */
  readonly leadsElsewhere: number;
  /** Households the same. */
  readonly householdsElsewhere: number;
}

/** The register, the latest first day first. `limit` is the server's cap, said out loud. */
export interface CampaignsReport {
  readonly today: string;
  readonly limit: number;
  readonly campaigns: readonly CampaignLine[];
}

export interface NewCampaign {
  readonly name: string;
  readonly channel: Channel;
  readonly platform: string | null;
  readonly plannedBudgetCents: number;
  readonly startsOn: string;
  readonly endsOn: string | null;
  readonly sourceDetail: string;
  readonly status: CampaignStatus;
  readonly notes: string | null;
}

/**
 * A correction. Every field is optional, and a nullable one sent as null is cleared. On a campaign
 * growth filed the server takes `notes` and nothing else.
 */
export interface CampaignPatch {
  readonly name?: string;
  readonly channel?: Channel;
  readonly platform?: string | null;
  readonly plannedBudgetCents?: number;
  readonly startsOn?: string;
  readonly endsOn?: string | null;
  readonly sourceDetail?: string;
  readonly status?: CampaignStatus;
  readonly notes?: string | null;
}

/**
 * One campaign as the register sends it, with none of its figures: what the spend screen needs to
 * offer a campaign as a choice and to say which one a row names. Only the columns it reads are
 * named, and a campaign thrown away never arrives here at all.
 */
export interface CampaignRow {
  readonly id: string;
  readonly name: string;
  readonly channel: Channel;
  readonly sourceDetail: string;
}

/** The register, the latest first day first. `limit` is the server's cap, said out loud. */
export interface CampaignList {
  readonly limit: number;
  readonly campaigns: readonly CampaignRow[];
}

/** Every live campaign in one read and no figures, for a screen that only needs to name one. */
export const listCampaigns = (): Promise<CampaignList> => request('/api/campaigns');

/** Every live campaign with its figures, in four statements however many there are. */
export const getCampaignsReport = (): Promise<CampaignsReport> => request('/api/reports/campaigns');

export const createCampaign = (body: NewCampaign): Promise<{ id: string }> =>
  send('/api/campaigns', 'POST', body);

export const patchCampaign = (id: string, body: CampaignPatch): Promise<{ ok: true }> =>
  send(`/api/campaigns/${encodeURIComponent(id)}`, 'PATCH', body);

/**
 * Soft delete with a reason, the expense discard's shape. A campaign holds no person, so the
 * server refuses a privacy request here and the screen never offers one.
 */
export const discardCampaign = (id: string, body: Discard): Promise<{ ok: true }> =>
  send(`/api/campaigns/${encodeURIComponent(id)}`, 'DELETE', body);

/* ------------------------------------------------------------------ *
 * Report B: the counters that have a threshold. Two doors, both reads,
 * both office and owner only.
 * ------------------------------------------------------------------ */

/** Every rule has an id, because one channel can carry two of them. */
export type KillRuleId =
  | 'local_services_leads'
  | 'thumbtack_leads'
  | 'search_ads_spend'
  | 'facebook_ads_spend'
  | 'chatgpt_ads_spend'
  | 'letter_december'
  | 'twice_the_planning_figure';

/** What `threshold` and `remaining` are counted in, so a screen prints them correctly. */
export type KillRuleUnit = 'leads' | 'cents' | 'households' | 'multiple';

export interface KillRuleRow {
  readonly rule: KillRuleId;
  /** The channel word, or the empty string on a rule that belongs to no single channel. */
  readonly channel: string;
  /** Verbatim, so the rule is read beside the number that judges it rather than remembered. */
  readonly ruleText: string;
  /** The counters this rule turns on. A counter nobody can compute is null and never zero. */
  readonly counters: Readonly<Record<string, number | null>>;
  readonly unit: KillRuleUnit;
  readonly threshold: number;
  /** How much is left before it fires. Null where the rule can no longer fire at all. */
  readonly remaining: number | null;
  /** The day it fires, or null where nothing can say one yet. */
  readonly firesOn: string | null;
  /** True where the day was projected from an observed rate rather than read off a date. */
  readonly projected: boolean;
  readonly withinReach: boolean;
  readonly note: string | null;
}

export interface KillRulesReport {
  readonly asOf: string;
  /**
   * The window the twice-the-figure rows are judged over: this month, to `asOf`. Written out
   * rather than named, because `web/src/reports.ts` already owns a `DayWindow` and two types of
   * one name in two files is how a screen ends up importing the wrong one.
   */
  readonly month: { readonly from: string; readonly to: string };
  readonly rules: readonly KillRuleRow[];
  readonly thresholdsReadOn: string;
  readonly thresholdsSource: string;
  readonly thresholdsHash: string;
  readonly planningReadOn: string;
  readonly planningSource: string;
}

/**
 * What the Today panel reads: the rules near firing, four fields each, and nothing else. Three
 * statements on the server, which is what lets it sit beside the four panels already on that
 * screen. The unit is deliberately not one of the four; `web/src/reports.ts` says how the number
 * is spelled and nothing here decides what any rule counts to.
 */
export interface WithinReachRow {
  readonly channel: string;
  readonly ruleText: string;
  readonly remaining: number | null;
  readonly firesOn: string | null;
}

/** The whole report. `asOf` defaults to today in the business zone, on the server. */
export const getKillRules = (asOf?: string): Promise<KillRulesReport> =>
  request(`/api/reports/kill-rules${query({ asOf })}`);

export const getKillRulesWithinReach = (): Promise<{ rules: readonly WithinReachRow[] }> =>
  request('/api/reports/kill-rules/within-reach');

/* ------------------------------------------------------------------ *
 * The asks from home the office decides (ops #19, step 7).
 * ------------------------------------------------------------------ */

/**
 * ONE ASK FROM HOME AS THE OFFICE READS IT (`docs/seams/home.md` section 4): what she asked, in her
 * words as she typed them, who she is and whether her door still stands, the visit or the home it is
 * about as they stand now, and where it is. `waiting` is a move the office accepted that waits for
 * her to be told, which home shows as sent to the office until the visit has moved.
 */
export interface HomeRequestView {
  readonly id: string;
  readonly requestId: string;
  readonly clientId: string;
  readonly kind: HomeRequestKind;
  readonly state: HomeRequestState;
  readonly occurredAt: string;
  readonly receivedAt: string;
  readonly payload: Readonly<Record<string, unknown>>;
  readonly answer: string | null;
  readonly decidedAt: string | null;
  readonly decidedBy: string | null;
  readonly decidedByName: string | null;
  readonly cancelReason: HomeRequestCancelReason | null;
  readonly taskId: string | null;
  readonly visitId: string | null;
  readonly siteId: string | null;
  readonly invoiceId: string | null;
  readonly person: { readonly contactId: string; readonly displayName: string };
  readonly membership: {
    readonly id: string;
    readonly state: MembershipState;
    readonly role: MembershipRole;
  };
  readonly visit: {
    readonly id: string;
    readonly scheduledDate: string;
    readonly windowStart: string | null;
    readonly windowEnd: string | null;
    readonly status: VisitStatus;
    readonly removed: boolean;
  } | null;
  readonly home: {
    readonly id: string;
    readonly label: string | null;
    readonly addressLine1: string;
    readonly doNotList: string | null;
    readonly pets: string | null;
    readonly entryNotes: string | null;
  } | null;
  readonly waiting: { readonly changeId: string; readonly toDate: string | null } | null;
}

/**
 * WHAT THE OFFICE DECIDED. `answer` is read by her on her page at home, so the server holds it to the
 * words test; a decline must carry one. The rest is by kind: a skip's charge decision and the
 * confirmation she will read, a move's day and the message that tells her, the person a `person.add`
 * became on the household, and a reply to her `message`.
 *
 * AND A SCHEDULE ASK'S (`docs/seams/home.md` section 12.4), in the words of the door the office uses
 * for the same thing asked on the phone, each left out to take what she asked: a pause's days and how
 * long her slot is held, a resume's day, an ending's last day, and a change's day it takes over from,
 * cadence, first clean on the new day (`null` for no new day, the hours alone), hours and, on a line
 * the card quotes after a walkthrough, the figure quoted her; `silenceReason`, the reason each clean
 * taken off carries; `message`, the news a change is to her, and `confirmation` beside the other three.
 */
export interface HomeDecision {
  readonly outcome: 'accepted' | 'declined';
  readonly answer?: string | null;
  readonly chargeDecision?: ChargeDecision | null;
  readonly confirmation?: ChangeMessage | null;
  readonly toDate?: string | null;
  readonly windowStartLocal?: string | null;
  readonly windowEndLocal?: string | null;
  readonly message?: ChangeMessage | null;
  readonly contactId?: string | null;
  readonly reply?: ChangeMessage | null;
  readonly silenceReason?: SilenceReason | null;
  readonly pausedFrom?: string | null;
  readonly pausedUntil?: string | null;
  readonly holdSlotUntil?: string | null;
  readonly resumeOn?: string | null;
  readonly endedOn?: string | null;
  readonly fromDate?: string | null;
  readonly cadence?: Cadence | null;
  readonly anchorDate?: string | null;
  readonly cleanerId?: string | null;
  readonly quotedPriceCents?: number | null;
}

export interface HomeDecided {
  readonly request: HomeRequestView;
  readonly change: ChangeResult | null;
  readonly message: {
    readonly intentId: string;
    readonly refusedByConsent: boolean;
    readonly gateRefusal: string | null;
    /** Her choice's or the office's hold's words (ops #76 phase 2), or null; absent from an older Worker. */
    readonly choiceRefusal?: string | null;
  } | null;
  readonly membership: MembershipRow | null;
}

/** A household's asks from home: the ones waiting on the office, or every one it decides. */
export const listHomeRequests = (
  clientId: string,
  state: 'open' | 'all' = 'open',
): Promise<{ requests: HomeRequestView[] }> =>
  request(`/api/home-requests${query({ clientId, state })}`);

export const getHomeRequest = (id: string): Promise<{ request: HomeRequestView }> =>
  request(`/api/home-requests/${encodeURIComponent(id)}`);

/** The decision, and every effect of it, written as the person pressing it. */
export const decideHomeRequest = (id: string, body: HomeDecision): Promise<HomeDecided> =>
  send(`/api/home-requests/${encodeURIComponent(id)}/decide`, 'POST', body);

/* ------------------------------------------------------------------ *
 * Call through the business line (ops #84 phase 6).
 * ------------------------------------------------------------------ */

/**
 * WHERE A REQUEST TO CALL STANDS (`GET /api/clients/:id/call/:requestId`): `asking` before its first
 * post, `not_confirmed` after a post the phone system did not confirm, `ringing` once it took it,
 * `refused` in its word, `expired` at its minute; and the call it became, once the phone system said it
 * started.
 */
export type CallRequestState = 'asking' | 'not_confirmed' | 'ringing' | 'refused' | 'expired';

export interface CallRequestView {
  readonly requestId: string;
  readonly contactId: string;
  readonly state: CallRequestState;
  /** The phone system's word for a refusal, `no_handset`, `line_busy` and the rest. */
  readonly code: string | null;
  /** Its own sentence for a refusal, as it gave it. */
  readonly phoneSaid: string | null;
  /** Whether an expired request was ever posted. */
  readonly attempted: boolean;
  readonly call: {
    readonly startedAt: string;
    readonly outcome: CallOutcome | null;
    readonly durationSeconds: number | null;
  } | null;
}

/**
 * CALL THROUGH THE BUSINESS LINE: the phone system rings the presser's own phone first, and on a key
 * pressed there, this person, who sees the business line. Office only; refused in words, with nothing
 * written but the check, for a person not to be called or while the line is not switched on.
 */
export const placeCall = (
  clientId: string,
  contactId: string,
): Promise<{ readonly requestId: string }> =>
  send(`/api/clients/${encodeURIComponent(clientId)}/call`, 'POST', { contactId });

/** Where a request to call stands, polled after the press. */
export const getCallRequest = (clientId: string, requestId: string): Promise<CallRequestView> =>
  request(`/api/clients/${encodeURIComponent(clientId)}/call/${encodeURIComponent(requestId)}`);
